import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Paperclip, AlertCircle, Calendar, CheckCircle, Circle, FileText, Image, ExternalLink, Eye, Sparkles, Pen, Package, ArrowUpDown, TrendingUp, TrendingDown, ClipboardList, MessageSquare, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import FileSizeErrorModal from './FileSizeErrorModal';
import { ComplianceTask } from '@/services/complianceTaskService';
import ImageGallery from './ImageGallery';
import { taskCompletionService, uploadEvidenceFile, AssetEvidenceEntry, AssetEvidenceItem } from '@/firebase/taskCompletionService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { formService, Form } from '@/firebase/formService';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { agreementService, AgreementDocument } from '@/firebase/agreementService';
import { agreementCompletionService, AgreementCompletion } from '@/firebase/agreementCompletionService';
import { complianceService, ComplianceTemplate, ReferenceDocument } from '@/firebase/complianceService';
import { assetService, Asset, AssetType } from '@/firebase/assetService';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { messagingIntegrationService } from '@/services/messagingIntegrationService';
import { parseDocument, DocumentAnalysis, calculateDueDateFromExpiry, isGeminiConfigured } from '@/services/geminiService';
import { calculateNextDueDate } from '@/services/complianceTaskService';
import SimpleSignatureCanvas from './SimpleSignatureCanvas';
import { createSignatureMetadata, uploadSignature } from '@/services/esignService';
import { trackTask } from '@/utils/analytics';
import { taskEvidenceDraftService, DraftEvidenceItem } from '@/firebase/taskEvidenceDraftService';

interface CompleteTaskModalProps {
  task: ComplianceTask;
  onComplete: (completionData: TaskCompletionData) => Promise<void>;
  onClose: () => void;
}

export interface TaskCompletionData {
  evidenceFiles: File[];
  comments: string;
  notes: string;
  completionDate: Date;
  manualNextDueDate?: Date | null;
  dueDateSelection?: 'ai' | 'manual' | 'auto';
}

const CompleteTaskModal: React.FC<CompleteTaskModalProps> = ({ task, onComplete, onClose }) => {
  const modalContainerRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const [taskConversationId, setTaskConversationId] = useState<string | null>(null);
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [comments, setComments] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  
  // AI Document Analysis states
  const [documentAnalyses, setDocumentAnalyses] = useState<Map<string, DocumentAnalysis>>(new Map());
  const documentAnalysesRef = useRef<Map<string, DocumentAnalysis>>(new Map()); // Ref to track latest analyses
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState<string | null>(null);
  const [pendingAnalyses, setPendingAnalyses] = useState<Set<string>>(new Set()); // Track files being analyzed
  const [fileNotes, setFileNotes] = useState<Map<string, string>>(new Map()); // Track notes for each file (keyed by file name)
  const [suggestedDueDate, setSuggestedDueDate] = useState<Date | null>(null);
  const [dueDateSelection, setDueDateSelection] = useState<'ai' | 'manual' | 'auto'>('auto');
  const [manualDueDateInput, setManualDueDateInput] = useState('');
  
  // Reference image viewer state
  const [viewingReferenceImageUrl, setViewingReferenceImageUrl] = useState<string | null>(null);
  const [viewingReferenceImageIndex, setViewingReferenceImageIndex] = useState<number>(0);
  const [viewingReferenceImageUrls, setViewingReferenceImageUrls] = useState<string[]>([]);
  
  // Keyboard navigation for reference image viewer
  useEffect(() => {
    if (!viewingReferenceImageUrl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setViewingReferenceImageUrl(null);
        setViewingReferenceImageUrls([]);
        setViewingReferenceImageIndex(0);
      } else if (e.key === 'ArrowLeft' && viewingReferenceImageIndex > 0) {
        const newIndex = viewingReferenceImageIndex - 1;
        setViewingReferenceImageIndex(newIndex);
        setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
      } else if (e.key === 'ArrowRight' && viewingReferenceImageIndex < viewingReferenceImageUrls.length - 1) {
        const newIndex = viewingReferenceImageIndex + 1;
        setViewingReferenceImageIndex(newIndex);
        setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingReferenceImageUrl, viewingReferenceImageIndex, viewingReferenceImageUrls]);
  
  // Checklist run history states
  const [checklistRuns, setChecklistRuns] = useState<ChecklistCompletion[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [templateChecklist, setTemplateChecklist] = useState<Checklist | null>(null);
  const [selectedRun, setSelectedRun] = useState<ChecklistCompletion | null>(null);
  
  // Form run history states
  const [formRuns, setFormRuns] = useState<FormCompletion[]>([]);
  const [isLoadingFormHistory, setIsLoadingFormHistory] = useState(false);
  const [templateForm, setTemplateForm] = useState<Form | null>(null);
  const [selectedFormRun, setSelectedFormRun] = useState<FormCompletion | null>(null);
  
  // Agreement run history states
  const [agreementRuns, setAgreementRuns] = useState<AgreementCompletion[]>([]);
  const [isLoadingAgreementHistory, setIsLoadingAgreementHistory] = useState(false);
  const [templateAgreements, setTemplateAgreements] = useState<AgreementDocument[]>([]);
  const [selectedAgreementRun, setSelectedAgreementRun] = useState<AgreementCompletion | null>(null);
  
  // Template and reference documents
  const [template, setTemplate] = useState<ComplianceTemplate | null>(null);
  const [referenceDocuments, setReferenceDocuments] = useState<ReferenceDocument[]>([]);
  
  // Asset-related states
  const [relatedAssets, setRelatedAssets] = useState<Asset[]>([]);
  const [assetCompletions, setAssetCompletions] = useState<{
    assetId: string;
    assetName: string;
    checklistCompletions: ChecklistCompletion[];
    formCompletions: FormCompletion[];
  }[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetSortBy, setAssetSortBy] = useState<'name' | 'completions' | 'pending'>('name');
  const [assetSortDirection, setAssetSortDirection] = useState<'asc' | 'desc'>('asc');
  const [assetEvidenceFiles, setAssetEvidenceFiles] = useState<Record<string, Record<string, File | null>>>({});
  const [assetEvidenceUrls, setAssetEvidenceUrls] = useState<Record<string, Record<string, DraftEvidenceItem>>>({});
  const [uploadingEvidenceKey, setUploadingEvidenceKey] = useState<string | null>(null);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  // File size error modal state
  const [fileSizeError, setFileSizeError] = useState<{ fileName: string; fileSize: number } | null>(null);

  const evidenceTypeDefinitions = task.evidenceTypes || [];

  const getEvidenceKey = (evidence: { type: string; label: string }, index: number) =>
    `${evidence.type || 'file'}_${index}`;

  // Load conversation for task
  useEffect(() => {
    const loadTaskConversation = async () => {
      if (!task?.id || !currentOrganization?.id) {
        setTaskConversationId(null);
        return;
      }
      
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const conversationsRef = collection(db, 'conversations');
        const conversationsQuery = query(
          conversationsRef,
          where('linkedItems.taskId', '==', task.id)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);
        
        let foundConversationId: string | null = null;
        conversationsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.linkedItems?.taskId === task.id) {
            foundConversationId = doc.id;
          }
        });
        
        setTaskConversationId(foundConversationId);
      } catch (error: any) {
        // If index error, conversation might not exist yet
        if (error.code !== 'failed-precondition' && !error.message?.includes('index')) {
          console.error('Error loading task conversation:', error);
        }
        setTaskConversationId(null);
      }
    };
    
    loadTaskConversation();
  }, [task?.id, currentOrganization?.id]);

  const matchesEvidenceType = (file: File, type: string) => {
    if (type === 'pdf') {
      return file.type === 'application/pdf';
    }
    if (type === 'photo') {
      return file.type.startsWith('image/');
    }
    return true;
  };

  const requiredEvidenceList = evidenceTypeDefinitions.filter(e => e.required);
  const hasRequiredEvidenceGeneral = requiredEvidenceList.every(ev => {
    const type = ev.type;
    return evidenceFiles.some(file =>
      matchesEvidenceType(file, type)
    );
  });

  const hasRequiredEvidenceForAssets = requiredEvidenceList.length === 0 || relatedAssets.length === 0
    ? true
    : relatedAssets.every(asset => {
        if (!asset.id) return true;
        const filesForAsset = assetEvidenceFiles[asset.id] || {};
        const urlsForAsset = assetEvidenceUrls[asset.id] || {};
        return requiredEvidenceList.every((evidence, index) => {
          const key = getEvidenceKey(evidence, index);
          const file = filesForAsset[key];
          const urlItem = urlsForAsset[key];
          return (file && matchesEvidenceType(file, evidence.type)) || !!urlItem;
        });
      });

  const hasRequiredEvidence = relatedAssets.length > 0 ? hasRequiredEvidenceForAssets : hasRequiredEvidenceGeneral;

  const loadTemplateReferenceDocuments = async () => {
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading template reference documents');
        return;
      }
      
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const foundTemplate = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (foundTemplate) {
          setTemplate(foundTemplate);
          setReferenceDocuments(foundTemplate.referenceDocuments || []);
        } else {
          console.warn('⚠️ Template not found:', { templateId: task.templateId, templateName: task.templateName });
        }
      }
    } catch (error) {
      console.error('Error loading template reference documents:', error);
    }
  };

  const handleViewCompletion = async (completion: ChecklistCompletion | FormCompletion, type: 'checklist' | 'form') => {
    if (type === 'checklist') {
      const checklistComp = completion as ChecklistCompletion;
      setSelectedRun(checklistComp);
      // Load the checklist template, but only if it matches the task's template
      if (checklistComp.checklistId) {
        const checklistResult = await checklistService.getChecklist(checklistComp.checklistId);
        if (checklistResult.success && checklistResult.data) {
          // Verify the checklist matches the task's template before displaying it
          if (task.templateId && checklistResult.data.templateId === task.templateId) {
            setTemplateChecklist(checklistResult.data);
          } else {
            console.log(`[CompleteTaskModal] Not displaying checklist "${checklistResult.data.name}" - templateId mismatch`, {
              checklistTemplateId: checklistResult.data.templateId,
              taskTemplateId: task.templateId
            });
            // Don't set templateChecklist if it doesn't match - just show the completion details
          }
        }
      }
    } else {
      const formComp = completion as FormCompletion;
      setSelectedFormRun(formComp);
      // Load the form template, but only if it matches the task's template
      if (formComp.formId) {
        const formResult = await formService.getForm(formComp.formId);
        if (formResult.success && formResult.data) {
          // Verify the form matches the task's template before displaying it
          if (task.templateId && formResult.data.templateId === task.templateId) {
            setTemplateForm(formResult.data);
          } else {
            console.log(`[CompleteTaskModal] Not displaying form "${formResult.data.name}" - templateId mismatch`, {
              formTemplateId: formResult.data.templateId,
              taskTemplateId: task.templateId
            });
            // Don't set templateForm if it doesn't match - just show the completion details
          }
        }
      }
    }
  };

  useEffect(() => {
    loadChecklistHistory();
    loadFormHistory();
    loadAgreementHistory();
    loadTemplateReferenceDocuments();
    loadRelatedAssets();
    
    // Check Gemini configuration on mount
    const geminiStatus = isGeminiConfigured();
    console.log('🔍 CompleteTaskModal mounted - Gemini configured:', geminiStatus);
    if (!geminiStatus) {
      console.warn('⚠️ Gemini AI is not configured. Document analysis will be disabled.');
      console.warn('⚠️ To enable: Add VITE_GEMINI_API_KEY to .env file and restart the server');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.templateName, task.locationId]);

  useEffect(() => {
    setSelectedRun(null);
    setSelectedFormRun(null);
    setSelectedAgreementRun(null);
    setSuggestedDueDate(null);
    setDueDateSelection('auto');
    setManualDueDateInput('');
  }, [task.id]);

  // Automatically select AI suggested date when it becomes available
  useEffect(() => {
    if (suggestedDueDate && dueDateSelection === 'auto') {
      console.log('✅ AI suggested due date available, automatically selecting AI option');
      setDueDateSelection('ai');
    }
  }, [suggestedDueDate, dueDateSelection]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const container = modalContainerRef.current;
      if (container) {
        container.focus();
        const firstInteractive = container.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        firstInteractive?.focus?.();
      }
    }, 25);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (relatedAssets.length === 0) {
      // Clear asset evidence when no assets
      if (Object.keys(assetEvidenceFiles).length > 0) {
        setAssetEvidenceFiles({});
      }
      return;
    }

    setAssetEvidenceFiles(prev => {
      let changed = false;
      const next: Record<string, Record<string, File | null>> = {};

      relatedAssets.forEach(asset => {
        if (!asset.id) {
          return;
        }
        if (!prev[asset.id]) {
          changed = true;
          next[asset.id] = {};
        } else {
          next[asset.id] = { ...prev[asset.id] };
        }
      });

      Object.keys(prev).forEach(assetId => {
        if (!relatedAssets.some(asset => asset.id === assetId)) {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [relatedAssets]);

  const loadChecklistHistory = async () => {
    setIsLoadingHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading checklist history');
        setIsLoadingHistory(false);
        return;
      }
      
      // Get the template ID from the task
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Filter checklists by template ID only - show only checklists associated with this compliance template
          const checklistsResult = await checklistService.getAllChecklists();
          if (checklistsResult.success && checklistsResult.data) {
            const associatedChecklists = checklistsResult.data.filter(c => {
              // Must have a templateId and it must match the task's template
              if (!c.templateId || c.templateId !== template.id) {
                console.log(`[CompleteTaskModal] Excluding checklist "${c.name}": templateId mismatch`, {
                  checklistTemplateId: c.templateId,
                  taskTemplateId: template.id
                });
                return false;
              }
              
              // If checklist has locationIds specified, must match task's location
              if (c.locationIds && c.locationIds.length > 0) {
                if (!task.locationId || !c.locationIds.includes(task.locationId)) {
                  console.log(`[CompleteTaskModal] Excluding checklist "${c.name}": location mismatch`, {
                    checklistLocationIds: c.locationIds,
                    taskLocationId: task.locationId
                  });
                  return false;
                }
              }
              
              console.log(`[CompleteTaskModal] Including checklist "${c.name}" for template "${template.id}"`);
              return true;
            });
            console.log(`[CompleteTaskModal] Found ${associatedChecklists.length} checklists for template "${template.id}" (${template.name})`, {
              checklists: associatedChecklists.map(c => ({ name: c.name, templateId: c.templateId }))
            });
            const associatedChecklist = associatedChecklists[0]; // Take first match
            
            if (associatedChecklist) {
              setTemplateChecklist(associatedChecklist);
              
              // Get all checklist completion history for this checklist
              const historyResult = await checklistCompletionService.getCompletionsForChecklist(associatedChecklist.id!);
              if (historyResult.success && historyResult.data) {
                // Filter checklist runs by the task's location and date range (createdAt to dueDate)
                // Handle various date formats (Date, Firestore Timestamp, number, string)
                const getDateFromValue = (value: any): Date => {
                  if (value instanceof Date) {
                    return value;
                  }
                  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                    return value.toDate();
                  }
                  if (typeof value === 'number') {
                    return new Date(value);
                  }
                  return new Date(value);
                };
                
                // Normalize start date to beginning of day
                const taskCreatedDate = (() => {
                  const date = getDateFromValue(task.createdAt);
                  date.setHours(0, 0, 0, 0);
                  return date;
                })();
                
                // For completed tasks, use completedAt/completedDate as end date; otherwise use dueDate
                // Normalize end date to end of day
                const taskEndDate = (() => {
                  const endDateValue = (task as any).completedAt || (task as any).completedDate || task.dueDate;
                  const date = getDateFromValue(endDateValue);
                  date.setHours(23, 59, 59, 999);
                  return date;
                })();
                
                const filteredRuns = historyResult.data.filter(run => {
                  // Filter by location
                  if (run.locationId !== task.locationId) {
                    return false;
                  }
                  
                  // Filter by date range - only show runs within task period
                  let runDate: Date | null = null;
                  if (run.completedAt?.toDate) {
                    runDate = run.completedAt.toDate();
                  } else if (typeof run.completedAt === 'number') {
                    runDate = new Date(run.completedAt);
                  } else if (run.completedAt instanceof Date) {
                    runDate = run.completedAt;
                  } else if (run.completedAt) {
                    runDate = new Date(run.completedAt);
                  }
                  
                  if (!runDate) {
                    return false; // Skip runs without valid dates
                  }
                  
                  // Run date must be within task period (createdAt to completedAt/dueDate)
                  return runDate >= taskCreatedDate && runDate <= taskEndDate;
                });
                
                setChecklistRuns(filteredRuns);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading checklist history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadFormHistory = async () => {
    setIsLoadingFormHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading form history');
        setIsLoadingFormHistory(false);
        return;
      }
      
      // Get the template ID from the task
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Find forms associated with this template
          const formsResult = await formService.getAllForms();
          if (formsResult.success && formsResult.data) {
            const associatedForm = formsResult.data.find(f => f.templateId === template.id);
            
            if (associatedForm) {
              setTemplateForm(associatedForm);
              
              // Get all form completion history for this form
              const historyResult = await formCompletionService.getCompletionsForForm(associatedForm.id!);
              if (historyResult.success && historyResult.data) {
                // Filter form runs by the task's location and date range (createdAt to dueDate)
                const getDateFromValue = (value: any): Date => {
                  if (value instanceof Date) {
                    return value;
                  }
                  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                    return value.toDate();
                  }
                  if (typeof value === 'number') {
                    return new Date(value);
                  }
                  return new Date(value);
                };
                
                // Normalize start date to beginning of day
                const taskCreatedDate = (() => {
                  const date = getDateFromValue(task.createdAt);
                  date.setHours(0, 0, 0, 0);
                  return date;
                })();
                
                // For completed tasks, use completedAt/completedDate as end date; otherwise use dueDate
                // Normalize end date to end of day
                const taskEndDate = (() => {
                  const endDateValue = (task as any).completedAt || (task as any).completedDate || task.dueDate;
                  const date = getDateFromValue(endDateValue);
                  date.setHours(23, 59, 59, 999);
                  return date;
                })();
                
                const filteredRuns = historyResult.data.filter(run => {
                  // Filter by location
                  if (run.locationId !== task.locationId) {
                    return false;
                  }
                  
                  // Filter by date range - only show runs within task period
                  let runDate: Date | null = null;
                  if (run.completionDate?.toDate) {
                    runDate = run.completionDate.toDate();
                  } else if (typeof run.completionDate === 'number') {
                    runDate = new Date(run.completionDate);
                  } else if (run.completionDate instanceof Date) {
                    runDate = run.completionDate;
                  } else if (run.completionDate) {
                    runDate = new Date(run.completionDate);
                  }
                  
                  if (!runDate) {
                    return false; // Skip runs without valid dates
                  }
                  
                  // Run date must be within task period (createdAt to completedAt/dueDate)
                  return runDate >= taskCreatedDate && runDate <= taskEndDate;
                });
                
                setFormRuns(filteredRuns);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading form history:', error);
    } finally {
      setIsLoadingFormHistory(false);
    }
  };

  const loadRelatedAssets = async () => {
    if (!currentOrganization?.id || !task.locationId || !task.templateId) {
      return;
    }
    
    setIsLoadingAssets(true);
    try {
      // Get all assets for the task's location
      const locationAssetsResult = await assetService.getAssetsByLocation(task.locationId, currentOrganization.id);
      if (!locationAssetsResult.success || !locationAssetsResult.data) {
        setRelatedAssets([]);
        return;
      }

      // Get all asset types to check compliance template associations
      const assetTypesResult = await assetService.getAllAssetTypes(currentOrganization.id);
      if (!assetTypesResult.success || !assetTypesResult.data) {
        setRelatedAssets([]);
        return;
      }

      // Filter assets whose assetType has this task's templateId in complianceTemplateIds
      const assetTypesMap = new Map(assetTypesResult.data.map(at => [at.id!, at]));
      const relatedAssetsList = locationAssetsResult.data.filter(asset => {
        const assetType = assetTypesMap.get(asset.assetTypeId);
        return assetType?.complianceTemplateIds?.includes(task.templateId);
      });

      // Sort assets by name initially
      relatedAssetsList.sort((a, b) => {
        const nameA = (a.assetName || '').toLowerCase();
        const nameB = (b.assetName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setRelatedAssets(relatedAssetsList);

      // OPTIMIZATION: Load completions ONCE for the location, not per asset
      const getDateFromValue = (value: any): Date => {
        if (value instanceof Date) return value;
        if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
          return value.toDate();
        }
        if (typeof value === 'number') return new Date(value);
        return new Date(value);
      };

      // Normalize start date to beginning of day (00:00:00.000)
      const taskCreatedDate = (() => {
        const date = getDateFromValue(task.createdAt);
        date.setHours(0, 0, 0, 0);
        return date;
      })();

      // For completed tasks, use completedAt/completedDate as end date; otherwise use dueDate
      // Normalize end date to end of day (23:59:59.999) to include all completions on that day
      const taskEndDate = (() => {
        const endDateValue = (task as any).completedAt || (task as any).completedDate || task.dueDate;
        const date = getDateFromValue(endDateValue);
        date.setHours(23, 59, 59, 999);
        return date;
      })();

      // Load all completions once, along with checklists and forms to filter by template
      const [allChecklistCompletionsResult, allFormCompletionsResult, checklistsResult, formsResult] = await Promise.all([
        checklistCompletionService.getCompletionsByLocation(task.locationId),
        formCompletionService.getCompletionsByLocation(task.locationId),
        checklistService.getAllChecklists(currentOrganization.id),
        formService.getAllForms(currentOrganization.id)
      ]);

      const allChecklistCompletions = allChecklistCompletionsResult.success && allChecklistCompletionsResult.data 
        ? allChecklistCompletionsResult.data 
        : [];
      const allFormCompletions = allFormCompletionsResult?.data || [];
      
      // Create maps of checklist/form IDs to their templateIds for filtering
      const checklistTemplateMap = new Map<string, string>();
      if (checklistsResult.success && checklistsResult.data) {
        checklistsResult.data.forEach(checklist => {
          if (checklist.id && checklist.templateId) {
            checklistTemplateMap.set(checklist.id, checklist.templateId);
          }
        });
      }
      
      const formTemplateMap = new Map<string, string>();
      if (formsResult.success && formsResult.data) {
        formsResult.data.forEach(form => {
          if (form.id && form.templateId) {
            formTemplateMap.set(form.id, form.templateId);
          }
        });
      }

      // Filter completions by date range AND template ID
      const dateFilteredChecklistCompletions = allChecklistCompletions.filter(cc => {
        // First filter by date range - only include completions within task period
        const completionDate = cc.completedAt?.toDate ? cc.completedAt.toDate() :
          (typeof cc.completedAt === 'number' ? new Date(cc.completedAt) :
          (cc.completedAt instanceof Date ? cc.completedAt : new Date()));
        
        // Exclude completions before task creation or after task end (completedAt or dueDate)
        if (completionDate < taskCreatedDate || completionDate > taskEndDate) {
          return false;
        }
        
        // Then filter by template ID - only include completions from checklists that match the task's template
        if (cc.checklistId) {
          const checklistTemplateId = checklistTemplateMap.get(cc.checklistId);
          if (checklistTemplateId === task.templateId) {
            return true;
          } else {
            console.log(`[CompleteTaskModal] Excluding checklist completion for asset - template mismatch`, {
              checklistId: cc.checklistId,
              checklistTemplateId,
              taskTemplateId: task.templateId
            });
            return false;
          }
        }
        
        // If no checklistId, exclude it
        return false;
      });

      const dateFilteredFormCompletions = allFormCompletions.filter(fc => {
        // First filter by date range - only include completions within task period
        const completionDate = fc.completionDate?.toDate ? fc.completionDate.toDate() :
          (typeof fc.completionDate === 'number' ? new Date(fc.completionDate) :
          (fc.completionDate instanceof Date ? fc.completionDate : new Date()));
        
        // Exclude completions before task creation or after task end (completedAt or dueDate)
        if (completionDate < taskCreatedDate || completionDate > taskEndDate) {
          return false;
        }
        
        // Then filter by template ID - only include completions from forms that match the task's template
        if (fc.formId) {
          const formTemplateId = formTemplateMap.get(fc.formId);
          if (formTemplateId === task.templateId) {
            return true;
          } else {
            console.log(`[CompleteTaskModal] Excluding form completion for asset - template mismatch`, {
              formId: fc.formId,
              formTemplateId,
              taskTemplateId: task.templateId
            });
            return false;
          }
        }
        
        // If no formId, exclude it
        return false;
      });

      // Create asset ID set for quick lookup
      const assetIds = new Set(relatedAssetsList.filter(a => a.id).map(a => a.id!));

      // Group completions by asset ID
      const completionsByAssetId = new Map<string, {
        checklistCompletions: ChecklistCompletion[];
        formCompletions: FormCompletion[];
      }>();

      // Initialize all assets with empty completions
      relatedAssetsList.forEach(asset => {
        if (asset.id) {
          completionsByAssetId.set(asset.id, {
            checklistCompletions: [],
            formCompletions: []
          });
        }
      });

      // Distribute checklist completions to assets
      dateFilteredChecklistCompletions.forEach(cc => {
        const assetId = (cc as any).assetId || 
                       (cc.variableValues && cc.variableValues['assetId']) ||
                       (cc.variableValues && cc.variableValues['asset']);
        if (assetId && assetIds.has(assetId)) {
          const existing = completionsByAssetId.get(assetId);
          if (existing) {
            existing.checklistCompletions.push(cc);
          }
        }
      });

      // Distribute form completions to assets
      dateFilteredFormCompletions.forEach(fc => {
        const assetId = (fc as any).assetId ||
                       (fc.fieldValues && fc.fieldValues['assetId']) ||
                       (fc.fieldValues && fc.fieldValues['asset']);
        if (assetId && assetIds.has(assetId)) {
          const existing = completionsByAssetId.get(assetId);
          if (existing) {
            existing.formCompletions.push(fc);
          }
        }
      });

      // Build completions data array
      const completionsData = relatedAssetsList
        .filter(asset => asset.id)
        .map(asset => {
          const completions = completionsByAssetId.get(asset.id!) || {
            checklistCompletions: [],
            formCompletions: []
          };
          return {
            assetId: asset.id!,
            assetName: asset.assetName,
            checklistCompletions: completions.checklistCompletions,
            formCompletions: completions.formCompletions
          };
        });

      setAssetCompletions(completionsData);

      const draftResult = await taskEvidenceDraftService.getDraft(task.id);
      if (draftResult.success && draftResult.data?.assetEvidence) {
        setAssetEvidenceUrls(draftResult.data.assetEvidence);
      }
    } catch (error) {
      console.error('Error loading related assets:', error);
      setRelatedAssets([]);
      setAssetCompletions([]);
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const loadAgreementHistory = async () => {
    setIsLoadingAgreementHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading agreement history');
        setIsLoadingAgreementHistory(false);
        return;
      }
      
      // Get the template ID from the task
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Find agreements associated with this template
          const agreementsResult = await agreementService.getAllDocuments(currentOrganization?.id);
          if (agreementsResult.success && agreementsResult.data) {
            const associatedAgreements = agreementsResult.data.filter(a => a.templateId === template.id);
            
            console.log('🔍 Agreement History Debug:', {
              templateId: template.id,
              templateName: template.name,
              taskLocationId: task.locationId,
              taskLocationName: task.locationName,
              foundAgreements: associatedAgreements.length,
              agreementIds: associatedAgreements.map(a => a.id)
            });
            
            if (associatedAgreements.length > 0) {
              setTemplateAgreements(associatedAgreements);
              
              // Get all agreement completions for these agreements
              const allCompletions: AgreementCompletion[] = [];
              
              for (const agreement of associatedAgreements) {
                if (agreement.id) {
                  const historyResult = await agreementCompletionService.getCompletionsForAgreement(agreement.id);
                  if (historyResult.success && historyResult.data) {
                    allCompletions.push(...historyResult.data);
                  }
                }
              }
              
              console.log('🔍 All completions before filtering:', {
                totalCompletions: allCompletions.length,
                completionLocations: allCompletions.map(c => ({ id: c.locationId, agreementId: c.agreementId }))
              });
              
              // Filter agreement runs by the task's location and date range (createdAt to dueDate)
              const getDateFromValue = (value: any): Date => {
                if (value instanceof Date) {
                  return value;
                }
                if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                  return value.toDate();
                }
                if (typeof value === 'number') {
                  return new Date(value);
                }
                if (value && typeof value === 'object' && ('_seconds' in value || 'seconds' in value)) {
                  const seconds = value._seconds || value.seconds || 0;
                  const nanoseconds = value._nanoseconds || value.nanoseconds || 0;
                  return new Date(seconds * 1000 + nanoseconds / 1000000);
                }
                return new Date(value);
              };
              
              const taskCreatedDate = getDateFromValue(task.createdAt);
              const taskDueDate = getDateFromValue(task.dueDate);
              
      const safeToISOString = (value: unknown): string | null => {
        const parsed = value instanceof Date ? value : new Date(value as any);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
      };

      console.log('🔍 Date range:', {
        taskCreatedDate: safeToISOString(taskCreatedDate),
        taskDueDate: safeToISOString(taskDueDate)
      });
              
              // Filter by location - prefer matching location, but show all if none match
              const locationFilteredRuns = allCompletions.filter(run => {
                // Compare locationId (case-insensitive string comparison)
                const runLocationId = run.locationId ? String(run.locationId).trim() : '';
                const taskLocationId = task.locationId ? String(task.locationId).trim() : '';
                
                if (runLocationId && taskLocationId) {
                  return runLocationId === taskLocationId;
                }
                
                // If no location IDs match, show all completions (more lenient)
                // This handles cases where location might not be set or there's a mismatch
                if (!runLocationId || !taskLocationId) {
                  return true;
                }
                
                // If location IDs don't match, still show them but log for debugging
                if (runLocationId !== taskLocationId) {
                  console.log('⚠️ Location ID mismatch, showing anyway:', {
                    runLocationId,
                    taskLocationId,
                    run: { id: run.id, agreementId: run.agreementId, signerName: run.signerName }
                  });
                  // Still show the agreement even if location doesn't match
                  return true;
                }
                
                return true;
              });
              
              console.log('🔍 After location filtering:', {
                locationFilteredCount: locationFilteredRuns.length,
                totalCompletions: allCompletions.length
              });
              
              // Filter by date range - show all recent agreements, be lenient with date range
              const filteredRuns = locationFilteredRuns.filter(run => {
                let runDate: Date | null = null;
                
                // Try to get date from signedAt first
                if (run.signedAt) {
                  try {
                    // Handle Firestore Timestamp explicitly
                    if (run.signedAt && typeof run.signedAt === 'object') {
                      // Check if it's a Firestore Timestamp with toDate
                      if ('toDate' in run.signedAt && typeof run.signedAt.toDate === 'function') {
                        runDate = run.signedAt.toDate();
                      }
                      // Check if it has _seconds property (Timestamp format)
                      else if ('_seconds' in run.signedAt || 'seconds' in run.signedAt) {
                        const seconds = run.signedAt._seconds || run.signedAt.seconds || 0;
                        const nanoseconds = run.signedAt._nanoseconds || run.signedAt.nanoseconds || 0;
                        runDate = new Date(seconds * 1000 + nanoseconds / 1000000);
                      }
                      // Check if it's a serverTimestamp placeholder
                      else if (run.signedAt._methodName === 'serverTimestamp') {
                        // Use clientSignedAt as fallback
                        if (run.clientSignedAt) {
                          runDate = new Date(run.clientSignedAt);
                        } else {
                          console.log('⚠️ serverTimestamp placeholder with no clientSignedAt for run:', run.id);
                          return false;
                        }
                      } else {
                        // Try generic date conversion
                        runDate = getDateFromValue(run.signedAt);
                      }
                    } else {
                      runDate = getDateFromValue(run.signedAt);
                    }
                  } catch (e) {
                    console.log('⚠️ Error parsing signedAt for run:', run.id, e);
                  }
                }
                
                // Fallback to clientSignedAt if signedAt didn't work
                if ((!runDate || isNaN(runDate.getTime())) && run.clientSignedAt) {
                  runDate = new Date(run.clientSignedAt);
                }
                
                if (!runDate || isNaN(runDate.getTime())) {
                  console.log('⚠️ Invalid date for run:', run.id, {
                    signedAt: run.signedAt,
                    signedAtType: typeof run.signedAt,
                    signedAtKeys: run.signedAt && typeof run.signedAt === 'object' ? Object.keys(run.signedAt) : 'N/A',
                    clientSignedAt: run.clientSignedAt
                  });
                  return false; // Skip runs without valid dates
                }
                
                // Be lenient with date range - show agreements within the task date range OR recent ones (within last 6 months)
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                
                const isInTaskRange = runDate >= taskCreatedDate && runDate <= taskDueDate;
                const isRecent = runDate >= sixMonthsAgo;
                
                // Show if in task range OR if recent (within last 6 months)
                const shouldShow = isInTaskRange || isRecent;
                
                if (!shouldShow) {
                  console.log('⚠️ Date out of range:', {
                    runDate: runDate.toISOString(),
                    taskCreatedDate: taskCreatedDate.toISOString(),
                    taskDueDate: taskDueDate.toISOString(),
                    sixMonthsAgo: sixMonthsAgo.toISOString(),
                    runId: run.id
                  });
                }
                
                return shouldShow;
              });
              
              console.log('🔍 Final filtered runs:', {
                finalCount: filteredRuns.length,
                runs: filteredRuns.map(r => ({ id: r.id, signedDate: r.signedAt, signerName: r.signerName }))
              });
              
              setAgreementRuns(filteredRuns);
            } else {
              console.log('⚠️ No agreements found for template:', template.id);
            }
          }
        } else {
          console.log('⚠️ Template not found for task:', task.templateName);
        }
      }
    } catch (error) {
      console.error('Error loading agreement history:', error);
    } finally {
      setIsLoadingAgreementHistory(false);
    }
  };

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;
    
    // Validate file sizes before adding to state
    const { validateFileSize, formatFileSize: formatFileSizeUtil, MAX_FILE_SIZE } = await import('@/utils/fileSizeValidation');
    const validFiles: File[] = [];
    const invalidFiles: { name: string; size: number }[] = [];
    
    files.forEach(file => {
      try {
        validateFileSize(file);
        validFiles.push(file);
      } catch (error) {
        invalidFiles.push({ name: file.name, size: file.size });
        console.error(`❌ File too large: ${file.name} (${formatFileSizeUtil(file.size)})`);
      }
    });
    
    // Show error modal for invalid files (show first invalid file)
    if (invalidFiles.length > 0) {
      const firstInvalid = invalidFiles[0];
      setFileSizeError({ fileName: firstInvalid.name, fileSize: firstInvalid.size });
    }
    
    // Only add valid files to state
    if (validFiles.length > 0) {
      setEvidenceFiles(prev => [...prev, ...validFiles]);
      
      // Analyze documents with Gemini if configured
      const geminiConfigured = isGeminiConfigured();
      console.log('🔍 Gemini configured?', geminiConfigured);
      
      if (geminiConfigured) {
        console.log('🤖 Gemini configured, analyzing documents...');
        // Analyze all files in parallel for faster results
      const filesToAnalyze = validFiles.filter(file => file.type.startsWith('image/') || file.type === 'application/pdf');
        console.log(`📄 Files to analyze: ${filesToAnalyze.length}`, filesToAnalyze.map(f => `${f.name} (${f.type})`));
        
        // Track which files are being analyzed
        setPendingAnalyses(prev => {
          const updated = new Set(prev);
          filesToAnalyze.forEach(f => updated.add(f.name));
          return updated;
        });
        
        // Start all analyses but don't block - they'll complete in the background
        for (const file of filesToAnalyze) {
          analyzeDocument(file).catch(error => {
            console.error(`❌ Error analyzing ${file.name}:`, error);
            // Remove from pending even on error
            setPendingAnalyses(prev => {
              const updated = new Set(prev);
              updated.delete(file.name);
              return updated;
            });
          });
        }
      } else {
        console.warn('⚠️ Gemini not configured - skipping document analysis');
        console.warn('⚠️ Check VITE_GEMINI_API_KEY in .env file and restart server');
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      await processFiles(newFiles);
      // Reset input so same file can be selected again if needed
      event.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const analyzeDocument = async (file: File) => {
    if (!isGeminiConfigured()) {
      console.warn(`⚠️ Gemini not configured, skipping analysis for ${file.name}`);
      setPendingAnalyses(prev => {
        const updated = new Set(prev);
        updated.delete(file.name);
        return updated;
      });
      return;
    }
    
    console.log(`🔬 Starting analysis for: ${file.name}`);
    setIsAnalyzing(true);
    setAnalyzingFile(file.name);
    
    try {
      const analysis = await parseDocument(file);
      
      // Store analysis for this file
      setDocumentAnalyses(prev => {
        const newMap = new Map(prev);
        newMap.set(file.name, analysis);
        documentAnalysesRef.current = newMap; // Update ref as well
        return newMap;
      });
      
      // If expiry date found, calculate suggested due date
      if (analysis.expiryDate) {
        const leadTimeDays = task.leadTimeDays || 30;
        const suggestedDate = calculateDueDateFromExpiry(analysis.expiryDate, leadTimeDays);
        setSuggestedDueDate(suggestedDate);
        // Automatically select AI suggested date when available
        setDueDateSelection('ai');
      }
      
      console.log(`✅ Document analyzed: ${file.name}`, {
        summary: analysis.summary.substring(0, 100) + '...',
        expiryDate: analysis.expiryDate,
        documentType: analysis.documentType
      });
    } catch (error) {
      console.error(`❌ Error analyzing document ${file.name}:`, error);
      // Store error in analysis
      setDocumentAnalyses(prev => {
        const newMap = new Map(prev);
        const errorAnalysis = {
          summary: `Analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          expiryDate: null,
          extractedDates: [],
          documentType: 'unknown',
          keyInformation: {},
        };
        newMap.set(file.name, errorAnalysis);
        documentAnalysesRef.current = newMap; // Update ref as well
        return newMap;
      });
    } finally {
      // Remove from pending analyses
      setPendingAnalyses(prev => {
        const updated = new Set(prev);
        updated.delete(file.name);
        
        // If this was the last file, clear analyzing state
        if (updated.size === 0) {
          setIsAnalyzing(false);
          setAnalyzingFile(null);
        }
        
        return updated;
      });
    }
  };

  const removeFile = (index: number) => {
    setEvidenceFiles(prev => {
      const fileToRemove = prev[index];
      if (fileToRemove) {
        // Remove from analyses and pending
        setDocumentAnalyses(prevAnalyses => {
          const updated = new Map(prevAnalyses);
          updated.delete(fileToRemove.name);
          documentAnalysesRef.current = updated; // Update ref as well
          return updated;
        });
        setPendingAnalyses(prevPending => {
          const updated = new Set(prevPending);
          updated.delete(fileToRemove.name);
          return updated;
        });
        // Also remove notes for this file
        setFileNotes(prev => {
          const updated = new Map(prev);
          updated.delete(fileToRemove.name);
          return updated;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleAssetEvidenceChange = (assetId: string, evidenceKey: string, file: File | null) => {
    if (!assetId) return;
    setAssetEvidenceFiles(prev => {
      const current = { ...(prev[assetId] || {}) };
      if (file) {
        current[evidenceKey] = file;
      } else {
        delete current[evidenceKey];
      }
      return {
        ...prev,
        [assetId]: current,
      };
    });
  };

  const handleAssetEvidenceInputChange = async (
    assetId: string,
    evidence: { type: string; label: string },
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    const key = getEvidenceKey(evidence, index);
    event.target.value = '';
    if (!file) {
      handleAssetEvidenceChange(assetId, key, null);
      return;
    }

    if (!matchesEvidenceType(file, evidence.type)) {
      const typeLabel =
        evidence.type === 'photo'
          ? 'a photo'
          : evidence.type === 'pdf'
            ? 'a PDF document'
            : `a ${evidence.type} file`;
      alert(`Please upload ${typeLabel} for "${evidence.label}".`);
      return;
    }

    setUploadingEvidenceKey(`${assetId}:${key}`);
    try {
      const ownerRole = (task as any).templateSnapshot?.owner_role || task.ownerRole;
      const uploadResult = await uploadEvidenceFile(file, {
        docType: 'asset_evidence',
        organizationId: currentOrganization?.id,
        locationId: task.locationId,
        taskId: task.id,
        templateId: task.templateId,
        assetId,
        uploadedBy: user ? { id: user.id, email: user.email, name: user.name } : undefined,
        title: `${task.templateName} - ${evidence.label}`,
        evidenceLabel: evidence.label,
        source: 'web',
        additionalMetadata: { evidenceType: evidence.type, ...(ownerRole && { owner_role: ownerRole }) },
      });
      const draftItem: DraftEvidenceItem = {
        url: uploadResult.url,
        fileName: file.name,
        uploadedAt: new Date().toISOString(),
      };
      const saved = await taskEvidenceDraftService.saveEvidence(
        task.id,
        assetId,
        key,
        draftItem,
        currentOrganization?.id
      );
      if (saved.success) {
        setAssetEvidenceUrls((prev) => ({
          ...prev,
          [assetId]: {
            ...(prev[assetId] || {}),
            [key]: draftItem,
          },
        }));
        showNotification('Evidence saved. It will remain even if you close without completing.', 'success');
      }
    } catch (err: any) {
      console.error('Evidence upload failed:', err);
      showNotification(err?.message || 'Upload failed', 'error');
    } finally {
      setUploadingEvidenceKey(null);
    }
  };

  const removeAssetEvidenceFile = async (assetId: string, evidenceKey: string) => {
    handleAssetEvidenceChange(assetId, evidenceKey, null);
    const urlItem = assetEvidenceUrls[assetId]?.[evidenceKey];
    if (urlItem) {
      const removed = await taskEvidenceDraftService.removeEvidence(task.id, assetId, evidenceKey);
      if (removed.success) {
        setAssetEvidenceUrls((prev) => {
          const next = { ...prev };
          const assetData = { ...(next[assetId] || {}) };
          delete assetData[evidenceKey];
          if (Object.keys(assetData).length === 0) delete next[assetId];
          else next[assetId] = assetData;
          return next;
        });
      }
    }
  };

  const renderAssetsTable = () => {
    const assetsWithCompletions = relatedAssets.map(asset => {
      const assetComp = assetCompletions.find(ac => ac.assetId === asset.id) || {
        assetId: asset.id!,
        assetName: asset.assetName,
        checklistCompletions: [],
        formCompletions: []
      };

      const allCompletions = [
        ...assetComp.checklistCompletions.map(cc => ({
          type: 'checklist' as const,
          id: cc.id || '',
          name: cc.checklistName,
          completionDate: cc.completedAt,
          completedBy: cc.completedBy,
          data: cc
        })),
        ...assetComp.formCompletions.map(fc => ({
          type: 'form' as const,
          id: fc.id || '',
          name: fc.formName,
          completionDate: fc.completionDate,
          completedBy: fc.completedBy,
          data: fc
        }))
      ];

      return {
        asset,
        assetComp,
        allCompletions
      };
    });

    let sortedAssets = [...assetsWithCompletions];
    const direction = assetSortDirection === 'asc' ? 1 : -1;

    if (assetSortBy === 'name') {
      sortedAssets.sort((a, b) => direction * a.asset.assetName.localeCompare(b.asset.assetName));
    } else if (assetSortBy === 'completions') {
      sortedAssets.sort((a, b) => direction * (b.allCompletions.length - a.allCompletions.length));
    } else if (assetSortBy === 'pending') {
      sortedAssets.sort((a, b) => direction * (a.allCompletions.length - b.allCompletions.length));
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (assetSortBy === 'name') {
                      setAssetSortDirection(assetSortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setAssetSortBy('name');
                      setAssetSortDirection('asc');
                    }
                  }}
                  className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                >
                  Asset Name
                  <ArrowUpDown className={`w-4 h-4 ${assetSortBy === 'name' ? 'text-primary-600' : 'text-gray-400'}`} />
                </button>
              </th>
              <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (assetSortBy === 'completions') {
                      setAssetSortDirection(assetSortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setAssetSortBy('completions');
                      setAssetSortDirection('desc');
                    }
                  }}
                  className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                >
                  Checklists & Forms
                  <ArrowUpDown className={`w-4 h-4 ${assetSortBy === 'completions' ? 'text-primary-600' : 'text-gray-400'}`} />
                </button>
              </th>
              {evidenceTypeDefinitions.length > 0 && (
                <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                  Required Evidence
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedAssets.map(({ asset, allCompletions }) => {
              const assetId = asset.id || '';
              const evidenceForAsset = assetId ? assetEvidenceFiles[assetId] || {} : {};
              const urlsForAsset = assetId ? assetEvidenceUrls[assetId] || {} : {};
              const evidenceStatuses = evidenceTypeDefinitions.map((evidence, idx) => {
                const key = getEvidenceKey(evidence, idx);
                const file = evidenceForAsset[key] || null;
                const urlItem = urlsForAsset[key] || null;
                const satisfied = (!!file && matchesEvidenceType(file, evidence.type)) || !!urlItem;
                return { evidence, key, file, urlItem, satisfied };
              });
              const assetHasMissingRequired = requiredEvidenceList.length > 0 && evidenceStatuses.some(status => status.evidence.required && !status.satisfied);

              return (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900">
                    {asset.assetName}
                    {asset.assetLocation && (
                      <span className="text-xs text-gray-500 ml-2">({asset.assetLocation})</span>
                    )}
                  </td>
                  <td className="px-4 py-3 border border-gray-200">
                    {allCompletions.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {allCompletions.map((completion, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (completion.type === 'checklist') {
                                handleViewCompletion(completion.data as ChecklistCompletion, 'checklist');
                              } else {
                                handleViewCompletion(completion.data as FormCompletion, 'form');
                              }
                            }}
                            className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors cursor-pointer flex items-center gap-1"
                          >
                            {completion.type === 'checklist' ? (
                              <ClipboardList className="w-3 h-3" />
                            ) : (
                              <FileText className="w-3 h-3" />
                            )}
                            {completion.name}
                            {completion.completionDate && (
                              <span className="text-blue-600 ml-1">
                                ({completion.completionDate?.toDate ? 
                                  completion.completionDate.toDate().toLocaleDateString() :
                                  (typeof completion.completionDate === 'number' ? 
                                    new Date(completion.completionDate).toLocaleDateString() :
                                    (completion.completionDate instanceof Date ? 
                                      completion.completionDate.toLocaleDateString() : ''))})
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No completions</span>
                    )}
                  </td>
                  {evidenceTypeDefinitions.length > 0 && (
                    <td className="px-4 py-3 border border-gray-200 align-top">
                      {assetId ? (
                        <div className="space-y-2">
                          {evidenceTypeDefinitions.map((evidence, idx) => {
                            const { key, file, urlItem, satisfied } = evidenceStatuses[idx];
                            const isRequired = evidence.required;
                            const hasEvidence = !!file || !!urlItem;
                            const isUploading = uploadingEvidenceKey === `${assetId}:${key}`;
                            return (
                              <div key={key} className="border border-gray-200 rounded-md p-2 bg-gray-50">
                                <div className="flex items-center justify-between gap-2 text-xs font-medium text-gray-800">
                                  <span>
                                    {evidence.label}{' '}
                                    <span className="text-gray-500 font-normal">({evidence.type})</span>
                                    {isRequired && <span className="text-red-600 ml-1">*</span>}
                                  </span>
                                  {satisfied ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                  ) : (
                                    isRequired && <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                                  )}
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                  <input
                                    type="file"
                                    accept={evidence.type === 'pdf' ? 'application/pdf' : evidence.type === 'photo' ? 'image/*' : '*'}
                                    onChange={(event) => handleAssetEvidenceInputChange(assetId, evidence, idx, event)}
                                    className="text-xs"
                                    disabled={isUploading}
                                  />
                                  {hasEvidence && (
                                    <button
                                      type="button"
                                      onClick={() => removeAssetEvidenceFile(assetId, key)}
                                      className="text-xs text-red-600 hover:text-red-800"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                                {hasEvidence ? (
                                  <div className="mt-1 text-[11px] text-gray-600 break-all flex items-center gap-2">
                                    <span>{file?.name ?? urlItem?.fileName}</span>
                                    {urlItem?.url && (
                                      <a
                                        href={urlItem.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                      >
                                        View
                                      </a>
                                    )}
                                  </div>
                                ) : isUploading ? (
                                  <div className="mt-1 text-[11px] text-gray-500 italic">Uploading...</div>
                                ) : (
                                  <div className="mt-1 text-[11px] text-gray-500 italic">
                                    {isRequired ? 'Required evidence pending' : 'Optional evidence'}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {assetHasMissingRequired && (
                            <div className="flex items-center gap-1 text-xs text-orange-600">
                              <AlertCircle className="w-3 h-3" />
                              <span>Upload required evidence for this asset.</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Asset ID unavailable</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required evidence
    if (requiredEvidenceList.length > 0) {
      const evidenceLabels = requiredEvidenceList.map(e => e.label).join(', ');
      if (relatedAssets.length > 0) {
        if (!hasRequiredEvidenceForAssets) {
          alert(`Please provide required evidence for each asset: ${evidenceLabels}`);
          return;
        }
      } else if (!hasRequiredEvidenceGeneral) {
        alert(`Please provide required evidence: ${evidenceLabels}`);
        return;
      }
    }

    setIsSubmitting(true);
    console.log('🔵 Starting task completion process...');
    console.log('🔵 Current documentAnalyses:', documentAnalyses);
    console.log('🔵 isAnalyzing:', isAnalyzing);
    console.log('🔵 analyzingFile:', analyzingFile);
    console.log('🔵 pendingAnalyses:', Array.from(pendingAnalyses));
    
    let manualNextDueDateValue: number | null = null;
    let manualNextDueDateDate: Date | null = null;
    let aiSuggestedDueDateValue: number | null = null;
    let dueDateOverrideSource: 'manual' | 'ai' | 'frequency' = 'frequency';
    const safeToISOStringForStorage = (value: unknown): string | null => {
      const parsed = value instanceof Date ? value : new Date(value as any);
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    };
    
    try {
      // Safety check: Wait for any in-progress analyses to complete (shouldn't be needed since button is disabled until analysis completes)
      // This is a defensive check in case of any edge cases or timing issues
      if (pendingAnalyses.size > 0) {
        console.warn(`⚠️ Unexpected: ${pendingAnalyses.size} analysis(es) still pending when submit was called. Waiting for completion...`);
        console.warn('⚠️ Files being analyzed:', Array.from(pendingAnalyses));
        
        let waitCount = 0;
        const maxWait = 10; // Reduced timeout since button should prevent this scenario
        while (pendingAnalyses.size > 0 && waitCount < maxWait) {
          await new Promise(resolve => setTimeout(resolve, 500));
          waitCount++;
        }
        
        if (pendingAnalyses.size > 0) {
          console.error(`❌ Analysis still in progress after ${maxWait * 0.5}s for ${pendingAnalyses.size} file(s). This should not happen!`);
          console.error('❌ Remaining files:', Array.from(pendingAnalyses));
        } else {
          console.log('✅ All analyses completed, proceeding with submission');
        }
      } else {
        console.log('✅ No pending analyses, proceeding with submission');
      }
      
      // Extract owner_role from task BEFORE uploading files
      // ComplianceTask has ownerRole directly, but we can also check if templateSnapshot exists (for TaskWithDetails)
      const ownerRole = (task as any).templateSnapshot?.owner_role || task.ownerRole;
      console.log('🔍 Extracted owner_role for task completion:', ownerRole);
      
      // Upload evidence files and get URLs
      console.log(`📤 ${evidenceFiles.length} file(s) selected`);
      const evidenceUrls: string[] = [];
      
      // Upload files if provided
      if (evidenceFiles.length > 0) {
        console.log('📤 Attempting to upload', evidenceFiles.length, 'file(s)...');
        for (const file of evidenceFiles) {
          console.log('📤 Uploading file:', file.name);
          try {
            const uploadResult = await uploadEvidenceFile(file, {
              docType: 'task_evidence',
              organizationId: currentOrganization?.id,
              locationId: task.locationId,
              taskId: task.id,
              templateId: task.templateId,
              uploadedBy: user
                ? { id: user.id, email: user.email, name: user.name }
                : undefined,
              title: `${task.templateName} - ${file.name}`,
              evidenceLabel: file.name,
              source: 'web',
              additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
            });
            evidenceUrls.push(uploadResult.url);
            console.log('✅ File uploaded successfully, URL:', uploadResult.url);
          } catch (uploadError: any) {
            console.error('❌ File upload failed:', uploadError);
            // Ask user if they want to continue without the file
            console.log(`⚠️ Upload failed for ${file.name}, but continuing with task completion`);
            // Store a placeholder to indicate the file was attempted but failed
            evidenceUrls.push(`UPLOAD_FAILED:${file.name}`);
          }
        }
      } else {
        console.log('ℹ️ No files selected - will save without evidence files');
      }

      // Save completion to database regardless of file upload status
      console.log('💾 Saving completion to database (with or without files)...');
      
      // Show evidence URLs that will be saved
      if (evidenceUrls.length > 0) {
        console.log('📎 Evidence references:', evidenceUrls);
      } else {
        console.log('📎 No evidence files to save');
      }
      
      const completedBy = user ? `${user.name} (${user.email})` : 'Unknown';
      const completionDate = new Date();
      
      console.log('👤 Completed by:', completedBy);
      console.log('📅 Completion date:', completionDate);
      
      // Skip due date logic for custom/ad-hoc tasks (one-time tasks)
      if (task.templateId) {
      if (dueDateSelection === 'manual') {
        if (!manualDueDateInput) {
          throw new Error('Please select a manual due date.');
        }
        const manualDate = new Date(`${manualDueDateInput}T00:00:00`);
        if (isNaN(manualDate.getTime())) {
          throw new Error('Invalid manual due date. Please adjust and try again.');
        }
        // Validate that manual date is not after the calculated frequency date
        if (calculatedFrequencyDueDate && manualDate > calculatedFrequencyDueDate) {
          const maxDateStr = calculatedFrequencyDueDate.toLocaleDateString();
          throw new Error(`Manual due date cannot be after ${maxDateStr} (based on template frequency). Please select an earlier date.`);
        }
        manualNextDueDateValue = manualDate.getTime();
        manualNextDueDateDate = manualDate;
        dueDateOverrideSource = 'manual';
      } else if (dueDateSelection === 'ai' && suggestedDueDate) {
        aiSuggestedDueDateValue = suggestedDueDate.getTime();
        dueDateOverrideSource = 'ai';
      } else {
        dueDateOverrideSource = 'frequency';
        }
      }
      
      // Handle signature if provided
      let signatureMetadata = undefined;
      if (signatureDataUrl) {
        try {
          // Try to upload signature to Firebase Storage
          const signatureUrl = await uploadSignature(
            signatureDataUrl,
            `signature_${task.id}_${Date.now()}.png`,
            {
              docType: 'task_signature',
              organizationId: currentOrganization?.id,
              locationId: task.locationId,
              taskId: task.id,
              templateId: task.templateId,
              uploadedBy: user
                ? { id: user.id, email: user.email, name: user.name }
                : undefined,
              title: `${task.templateName} - Signature`,
              source: 'web',
              additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
            }
          );
          signatureMetadata = createSignatureMetadata(
            signatureDataUrl,
            signerName || user?.name || undefined,
            signerEmail || user?.email || undefined,
            'draw'
          );
          signatureMetadata.signatureUrl = signatureUrl;
          console.log('✅ Signature uploaded:', signatureUrl);
        } catch (signatureError) {
          console.warn('⚠️ Signature upload failed, storing as data URL:', signatureError);
          // Fallback to storing as data URL if upload fails
          signatureMetadata = createSignatureMetadata(
            signatureDataUrl,
            signerName || user?.name || undefined,
            signerEmail || user?.email || undefined,
            'draw'
          );
        }
      }
      
      // Prepare document analyses for storage (convert Dates to ISO strings for Firestore)
      // Note: We use documentAnalysesRef to ensure we get the latest analysis results,
      // even if React state hasn't fully updated yet (defensive programming)
      const documentAnalysesForStorage: Record<string, any> = {};
      const evidenceFileNames: string[] = [];
      const fileNotesForStorage: Record<string, string> = {}; // Store notes for each file
      
      if (evidenceFiles.length > 0) {
        evidenceFiles.forEach((file, idx) => {
          if (idx < evidenceUrls.length) {
            evidenceFileNames.push(file.name);
            // Get the latest analysis from the ref (always has the most up-to-date value)
            // Fallback to state if ref is empty (shouldn't happen, but safety check)
            const analysis = documentAnalysesRef.current.get(file.name) || documentAnalyses.get(file.name);
            console.log(`📝 Processing file ${idx}: ${file.name}, has analysis: ${!!analysis}`);
            if (analysis) {
              const expiryDateIso = safeToISOStringForStorage(analysis.expiryDate);
              const extractedDatesForStorage = (Array.isArray(analysis.extractedDates) ? analysis.extractedDates : [])
                .map((d: any) => {
                  const extractedDateIso = safeToISOStringForStorage(d?.date);
                  if (!extractedDateIso) return null;
                  return {
                    type: d?.type || 'other',
                    date: extractedDateIso,
                    confidence: d?.confidence || 'medium',
                  };
                })
                .filter(Boolean);

              documentAnalysesForStorage[file.name] = {
                summary: analysis.summary,
                expiryDate: expiryDateIso,
                extractedDates: extractedDatesForStorage,
                documentType: analysis.documentType,
                keyInformation: analysis.keyInformation,
              };
              console.log(`✅ Saved analysis for ${file.name}:`, documentAnalysesForStorage[file.name]);
            } else {
              console.warn(`⚠️ No analysis found for ${file.name} - analysis may not have completed in time`);
              console.warn(`⚠️ File was uploaded but analysis is missing. This can happen if:`);
              console.warn(`   - The analysis timed out (>60s)`);
              console.warn(`   - The analysis failed silently`);
              console.warn(`   - The file was submitted before analysis completed`);
            }
            
            // Save notes for this file if they exist
            const note = fileNotes.get(file.name);
            if (note && note.trim()) {
              fileNotesForStorage[file.name] = note.trim();
            }
          }
        });
      }
      
      // Build asset evidence from files (upload now) and persisted draft URLs (use as-is)
      const assetEvidenceUploads: AssetEvidenceEntry[] = [];
      if (relatedAssets.length > 0) {
        for (const asset of relatedAssets) {
          if (!asset.id) continue;
          const evidenceForAsset = assetEvidenceFiles[asset.id] || {};
          const urlsForAsset = assetEvidenceUrls[asset.id] || {};
          const evidenceItems: AssetEvidenceItem[] = [];

          for (let idx = 0; idx < evidenceTypeDefinitions.length; idx++) {
            const evidenceDefinition = evidenceTypeDefinitions[idx];
            const evidenceKey = getEvidenceKey(evidenceDefinition, idx);
            const file = evidenceForAsset[evidenceKey];
            const urlItem = urlsForAsset[evidenceKey];
            if (!file && !urlItem) continue;

            let url: string;
            let fileName: string;
            if (urlItem) {
              url = urlItem.url;
              fileName = urlItem.fileName;
              console.log(`📎 Using persisted evidence for ${asset.assetName} - ${evidenceDefinition.label}`);
            } else if (file) {
              console.log(`📤 Uploading asset evidence for ${asset.assetName} - ${evidenceDefinition.label}`);
              try {
                const uploadResult = await uploadEvidenceFile(file, {
                  docType: 'asset_evidence',
                  organizationId: currentOrganization?.id,
                  locationId: task.locationId,
                  taskId: task.id,
                  templateId: task.templateId,
                  assetId: asset.id,
                  uploadedBy: user
                    ? { id: user.id, email: user.email, name: user.name }
                    : undefined,
                  title: `${asset.assetName || asset.assetLocation || asset.id} - ${evidenceDefinition.label}`,
                  evidenceLabel: evidenceDefinition.label,
                  source: 'web',
                  additionalMetadata: {
                    assetName: asset.assetName,
                    assetLocation: asset.assetLocation,
                    evidenceType: evidenceDefinition.type,
                    ...(ownerRole && { owner_role: ownerRole }),
                  },
                });
                url = uploadResult.url;
                fileName = file.name;
                console.log('✅ Asset evidence uploaded successfully:', url);
              } catch (uploadError: any) {
                console.error('❌ Asset evidence upload failed:', uploadError);
                url = `UPLOAD_FAILED:${file.name}`;
                fileName = file.name;
              }
            } else {
              continue;
            }

            evidenceItems.push({
              key: evidenceKey,
              label: evidenceDefinition.label,
              type: evidenceDefinition.type,
              url,
              fileName,
            });
          }

          if (evidenceItems.length > 0) {
            assetEvidenceUploads.push({
              assetId: asset.id,
              assetName: asset.assetName,
              evidence: evidenceItems,
            });
          }
        }
      }

      console.log('💾 Final documentAnalysesForStorage:', documentAnalysesForStorage);
      console.log('💾 Final evidenceFileNames:', evidenceFileNames);
      console.log('💾 documentAnalyses Map size:', documentAnalyses.size);
      console.log('💾 documentAnalyses Map keys:', Array.from(documentAnalyses.keys()));
      console.log('💾 Asset evidence uploads:', assetEvidenceUploads);

      // Prepare completion data, only including fields that have values
      // Note: ownerRole was already extracted above before file uploads
      const completionData: any = {
        taskId: task.id,
        templateName: task.templateName,
        locationName: task.locationName,
        locationId: task.locationId,
        evidenceUrls,
        comments,
        notes,
        completionDate: completionDate,
        completedBy: completedBy,
      };
      
      // Track task completion
      trackTask.completed(task.id, task.templateName, task.locationId);
      
      // Add owner_role if available
      if (ownerRole) {
        completionData.owner_role = ownerRole;
      }

      // Only add optional fields if they have values
      if (evidenceFileNames.length > 0) {
        completionData.evidenceFileNames = evidenceFileNames;
      }

      if (Object.keys(documentAnalysesForStorage).length > 0) {
        completionData.documentAnalyses = documentAnalysesForStorage;
        console.log('✅ Adding documentAnalyses to completion data:', completionData.documentAnalyses);
      } else {
        console.warn('⚠️ No document analyses to save. Analysis map:', documentAnalyses);
      }

      // Add file notes if any exist
      if (Object.keys(fileNotesForStorage).length > 0) {
        completionData.fileNotes = fileNotesForStorage;
        console.log('✅ Adding fileNotes to completion data:', completionData.fileNotes);
      }

      if (assetEvidenceUploads.length > 0) {
        completionData.assetEvidence = assetEvidenceUploads;
        console.log('✅ Adding assetEvidence to completion data:', completionData.assetEvidence);
      }

      // Always save dueDateOverrideSource if set (manual, ai, or frequency)
      completionData.dueDateOverrideSource = dueDateOverrideSource;
      if (manualNextDueDateValue !== null) {
        completionData.manualNextDueDate = manualNextDueDateValue;
      }
      if (aiSuggestedDueDateValue !== null) {
        completionData.aiSuggestedDueDate = aiSuggestedDueDateValue;
      }

      // Add signature if provided
      if (signatureMetadata) {
        // Convert signedAt date to timestamp for Firestore
        const signatureForStorage = {
          ...signatureMetadata,
          signedAt: signatureMetadata.signedAt instanceof Date 
            ? signatureMetadata.signedAt.getTime() 
            : new Date().getTime(),
        };
        completionData.signature = signatureForStorage;
        console.log('✅ Adding signature to completion data');
      }

      const completionResult = await taskCompletionService.addCompletion(completionData);

      console.log('📊 Completion result:', completionResult);

      if (completionResult.success) {
        console.log('✅ Task completion saved to database:', completionResult.id);
        console.log('✅ Task ID:', task.id);
        console.log('✅ Template:', task.templateName);
        console.log('✅ Location:', task.locationName);
        
        // Call the callback
        console.log('🔄 Calling onComplete callback...');
        await onComplete({
          evidenceFiles,
          comments,
          notes,
          completionDate: new Date(),
          manualNextDueDate:
            dueDateSelection === 'manual'
              ? manualNextDueDateDate
              : dueDateSelection === 'ai' && suggestedDueDate
                ? suggestedDueDate
                : null,
          dueDateSelection: dueDateSelection,
        });
        
        await taskEvidenceDraftService.clearDraft(task.id);
        console.log('✅ Task completion process completed successfully');
        console.log('🎉 Closing modal...');
        onClose();
      } else {
        console.error('❌ Failed to save completion:', completionResult.error);
        throw new Error(completionResult.error || 'Failed to save completion');
      }
    } catch (error) {
      console.error('❌ Error completing task:', error);
      alert(`Failed to complete task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      console.log('🏁 Completing task process finished');
      setIsSubmitting(false);
    }
  };

  const handleOpenTaskConversation = async () => {
    if (!currentOrganization?.id || !user?.id || !task.id) {
      showNotification('Unable to open task conversation. Please ensure you are logged in.', 'error');
      return;
    }

    try {
      console.log('Opening task conversation for task:', task.id);
      const result = await messagingIntegrationService.getOrCreateTaskConversation(
        task.id,
        task.locationId || '',
        task.templateName || 'Task',
        currentOrganization.id,
        user.id,
        task.ownerRole
      );

      if (result.success && result.data?.id) {
        console.log('Opening conversation:', result.data.id);
        // Open messages page in a new window/tab with conversation ID
        window.open(`/messages?conversation=${result.data.id}`, '_blank');
      } else {
        console.error('Failed to get/create conversation:', result.error);
        showNotification(result.error || 'Failed to open task conversation', 'error');
      }
    } catch (error: any) {
      console.error('Error opening task conversation:', error);
      showNotification(error.message || 'Failed to open task conversation', 'error');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const ensureDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value && typeof value === 'object' && typeof value.toDate === 'function') {
      return value.toDate();
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    return null;
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const container = modalContainerRef.current;
      if (container) {
        container.focus();
        const firstInteractive = container.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        firstInteractive?.focus?.();
      }
    }, 25);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    setSelectedRun(null);
    setSelectedFormRun(null);
    setSelectedAgreementRun(null);
  }, [task.id]);

  useEffect(() => {
    if (suggestedDueDate) {
      // If AI date is available, default to 'ai' unless user already selected 'manual'
      setDueDateSelection(prev => (prev === 'manual' ? 'manual' : 'ai'));
    } else {
      // If no AI date, default to 'auto' (template frequency)
      setDueDateSelection('auto');
      setManualDueDateInput('');
    }
  }, [suggestedDueDate]);

  useEffect(() => {
    // When user selects 'manual', set default date if not already set
    if (dueDateSelection === 'manual' && !manualDueDateInput) {
      const defaultDate = suggestedDueDate || ensureDate(task.dueDate);
      if (defaultDate) {
        setManualDueDateInput(formatDateForInput(defaultDate));
      }
    }
  }, [dueDateSelection, suggestedDueDate, task.dueDate, manualDueDateInput]);

  // Calculate next due date based on template frequency
  const calculatedFrequencyDueDate = React.useMemo(() => {
    const frequency = task.frequency || template?.frequency || 'P1Y';
    const baseDate = ensureDate(task.dueDate);
    if (!baseDate) return null;
    try {
      return calculateNextDueDate(baseDate, frequency);
    } catch (error) {
      console.error('Error calculating frequency-based due date:', error);
      return null;
    }
  }, [task.dueDate, task.frequency, template?.frequency]);

  // Format calculated frequency date for date input max attribute (YYYY-MM-DD)
  const maxDateInput = React.useMemo(() => {
    if (!calculatedFrequencyDueDate) return undefined;
    return formatDateForInput(calculatedFrequencyDueDate);
  }, [calculatedFrequencyDueDate]);

  const handleDueDateSelectionChange = (value: 'ai' | 'manual' | 'auto') => {
    setDueDateSelection(value);
    // When user selects 'manual', set default date if not already set
    if (value === 'manual' && !manualDueDateInput) {
      const defaultDate = suggestedDueDate || ensureDate(task.dueDate);
      if (defaultDate) {
        setManualDueDateInput(formatDateForInput(defaultDate));
      }
    } else if (value !== 'manual') {
      // Clear manual input when switching away from manual
      setManualDueDateInput('');
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-600 bg-opacity-60 flex justify-center items-center z-[210] p-4"
        data-testid="complete-task-modal"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        ref={modalContainerRef}
      >
        {/* Backdrop overlay when run details panel is open - fades only the background */}
        {selectedRun && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-20 z-[51]"
            onClick={() => setSelectedRun(null)}
          />
        )}
        <div className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto transition-all relative z-[211] ${
          selectedRun ? 'max-w-2xl' : 'max-w-3xl'
        }`}>
        <div className="p-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Complete Task</h3>
              <p className="text-xs text-gray-600 mt-0.5">{task.templateName}</p>
              <p className="text-[11px] text-gray-500 mt-1 font-mono">
                Task ID: {task.id} • Start: {(() => {
                  const v = task.createdAt;
                  if (!v) return '—';
                  if (v instanceof Date) return v.toLocaleDateString();
                  if (typeof v === 'number') return new Date(v).toLocaleDateString();
                  if (typeof v === 'object' && 'toDate' in v && typeof (v as any).toDate === 'function') return (v as any).toDate().toLocaleDateString();
                  return '—';
                })()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleOpenTaskConversation}
                className={`p-1 ${
                  task?.id && taskConversationId
                    ? 'text-blue-600 hover:text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title={taskConversationId ? 'Open Task Conversation (conversation exists)' : 'Open Task Conversation'}
              >
                <MessageSquare className="w-5 h-5" />
              </button>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Reference Images */}
          {task.referenceImageUrls && task.referenceImageUrls.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                <Image className="w-3 h-3 text-blue-600" />
                Reference Images
              </h4>
              <p className="text-xs text-gray-600 mb-2">
                Review these reference images while completing the task
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {task.referenceImageUrls.map((imageUrl, index) => {
                  const imageMetadata = task.referenceImageMetadata?.find(meta => meta.url === imageUrl);
                  const note = imageMetadata?.note;
                  const fileName = imageMetadata?.fileName || task.referenceImageFileNames?.[index] || `Image ${index + 1}`;
                  
                  return (
                    <div key={index} className="flex gap-3 p-2 bg-white rounded border border-blue-200 hover:border-blue-300 transition-colors">
                      {/* Image Thumbnail on Left */}
                      <div className="flex-shrink-0 relative group">
                        <img
                          src={imageUrl}
                          alt={fileName}
                          className="w-24 h-24 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setViewingReferenceImageUrls(task.referenceImageUrls || []);
                            setViewingReferenceImageIndex(index);
                            setViewingReferenceImageUrl(imageUrl);
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded flex items-center justify-center transition-opacity cursor-pointer"
                          onClick={() => {
                            setViewingReferenceImageUrls(task.referenceImageUrls || []);
                            setViewingReferenceImageIndex(index);
                            setViewingReferenceImageUrl(imageUrl);
                          }}
                        >
                          <Maximize2 className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      {/* Note on Right */}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-700 mb-1">
                          {fileName}
                        </div>
                        {note ? (
                          <div className="text-xs text-gray-600 whitespace-pre-wrap">
                            {note}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic">
                            No note provided
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reference Documents */}
          {referenceDocuments.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
              <h4 className="text-xs font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-blue-600" />
                Reference Documents
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {referenceDocuments.map((doc, index) => (
                  <a
                    key={index}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-1.5 bg-white rounded border border-blue-200 hover:border-blue-400 transition-all cursor-pointer text-xs"
                  >
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      {doc.type === 'pdf' ? (
                        <FileText className="w-3 h-3 text-red-600 flex-shrink-0" />
                      ) : (
                        <Image className="w-3 h-3 text-green-600 flex-shrink-0" />
                      )}
                      <span className="text-gray-900 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                      <span className="text-gray-500 flex-shrink-0">
                        ({doc.type.toUpperCase()})
                      </span>
                    </div>
                    <ExternalLink className="w-3 h-3 text-blue-600 flex-shrink-0 ml-1" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Task Details */}
          <div className="bg-gray-50 p-2 rounded-lg space-y-1.5">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-xs text-gray-500">Location</span>
                <p className="text-xs font-medium text-gray-900">{task.locationName}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Category</span>
                <p className="text-xs font-medium text-gray-900">{task.category}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Owner Role</span>
                <p className="text-xs font-medium text-gray-900">{task.ownerRole}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Risk Level</span>
                <p className="text-xs font-medium text-gray-900">{task.riskLevel}</p>
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Pass/Fail Criteria</span>
              <p className="text-xs text-gray-900 mt-0.5">{task.passFailCriteria}</p>
            </div>
          </div>

          {/* Evidence Types */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Required Evidence
            {evidenceTypeDefinitions.some(e => e.required) && (
                <span className="text-red-600 ml-1">*</span>
              )}
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`space-y-1.5 p-2 rounded-lg border-2 border-dashed transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400'
              }`}
            >
              {isDragging ? (
                <div className="text-center py-2 text-xs text-blue-600 font-medium">
                  Drop files here to upload
                </div>
              ) : (
                <div className="text-center py-2 text-xs text-gray-500">
                  Drag and drop files here{evidenceTypeDefinitions.length > 0 ? ' or use the file inputs below' : ''}
                </div>
              )}
              {evidenceTypeDefinitions.length > 0 && evidenceTypeDefinitions.map((evidence, idx) => (
                <div key={idx} className="flex items-center justify-between p-1.5 bg-white rounded text-xs border border-gray-200">
                  <div className="flex items-center gap-1.5">
                    <Paperclip className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-900">
                      {evidence.label} ({evidence.type})
                      {evidence.required && <span className="text-red-600 ml-1">*</span>}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept={evidence.type === 'pdf' ? 'application/pdf' : evidence.type === 'photo' ? 'image/*' : '*'}
                    onChange={handleFileChange}
                    className="text-xs max-w-[140px]"
                    multiple={false}
                  />
                </div>
              ))}
              {evidenceTypeDefinitions.length === 0 && (
                <div className="flex items-center justify-between p-1.5 bg-white rounded text-xs border border-gray-200">
                  <div className="flex items-center gap-1.5">
                    <Paperclip className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-900">Upload Evidence Files</span>
                  </div>
                  <input
                    type="file"
                    accept="*"
                    onChange={handleFileChange}
                    className="text-xs max-w-[140px]"
                    multiple={true}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Uploaded Files List */}
          {evidenceFiles.length > 0 && (
            <div className="mt-1.5 space-y-2">
              <label className="block text-xs font-medium text-gray-700">Uploaded Files</label>
              {evidenceFiles.map((file, idx) => {
                  const analysis = documentAnalyses.get(file.name);
                  const isImage = file.type.startsWith('image/');
                  const previewUrl = isImage ? URL.createObjectURL(file) : null;
                  const fileNote = fileNotes.get(file.name);
                  
                  return (
                    <div key={idx} className="p-2 bg-blue-50 rounded text-xs border border-blue-200">
                      <div className="flex items-start justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <Upload className="w-3 h-3 text-blue-600 flex-shrink-0" />
                          <span className="text-gray-900 truncate">{file.name}</span>
                          <span className="text-gray-500 flex-shrink-0">({formatFileSize(file.size)})</span>
                          {analyzingFile === file.name && (
                            <span className="text-xs text-blue-600 flex items-center gap-1 flex-shrink-0">
                              <div className="animate-spin rounded-full h-2 w-2 border-b border-blue-600"></div>
                              Analyzing...
                            </span>
                          )}
                          {analysis && !analyzingFile && (
                            <Sparkles className="w-3 h-3 text-purple-600 flex-shrink-0" />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (previewUrl) URL.revokeObjectURL(previewUrl);
                            removeFile(idx);
                            // Also remove analysis when file is removed
                            setDocumentAnalyses(prev => {
                              const newMap = new Map(prev);
                              newMap.delete(file.name);
                              return newMap;
                            });
                          }}
                          className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                        >
                          Remove
                        </button>
                      </div>
                      
                      {/* Image Preview and Analysis Layout */}
                      <div className={`flex gap-2 ${isImage ? 'flex-row' : 'flex-col'}`}>
                        {/* Image Preview (Left) */}
                        {isImage && previewUrl && (
                          <div className="flex-shrink-0 w-32 h-32 border border-gray-300 rounded bg-white flex items-center justify-center overflow-hidden">
                            <img 
                              src={previewUrl} 
                              alt={file.name}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        )}
                        
                        {/* Analysis and Notes (Right) */}
                        <div className={`flex-1 min-w-0 ${isImage ? '' : 'mt-1.5'}`}>
                          {/* Document Analysis Results */}
                          {analysis && (
                            <div className="mt-1.5 p-1.5 bg-white rounded border border-purple-200">
                              <div className="text-xs space-y-1">
                                <div>
                                  <span className="font-medium text-purple-700">Type:</span>{' '}
                                  <span className="text-gray-900">{analysis.documentType}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-purple-700">Summary:</span>{' '}
                                  <span className="text-gray-900">{analysis.summary}</span>
                                </div>
                                {analysis.expiryDate && (
                                  <div className="bg-orange-50 border border-orange-200 rounded p-1 mt-1">
                                    <span className="font-medium text-orange-700">Expiry Date Found:</span>{' '}
                                    <span className="text-gray-900">{analysis.expiryDate.toLocaleDateString()}</span>
                                  </div>
                                )}
                                {analysis.extractedDates.length > 0 && (
                                  <div className="mt-1">
                                    <span className="font-medium text-purple-700">All Dates:</span>
                                    <div className="space-y-0.5 mt-0.5">
                                      {analysis.extractedDates.map((dateInfo, dIdx) => (
                                        <div key={dIdx} className="text-gray-700 text-xs">
                                          {dateInfo.type}: {dateInfo.date.toLocaleDateString()} ({dateInfo.confidence} confidence)
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {Object.keys(analysis.keyInformation).length > 0 && (
                                  <div className="mt-1">
                                    <span className="font-medium text-purple-700">Key Info:</span>
                                    <div className="space-y-0.5 mt-0.5">
                                      {Object.entries(analysis.keyInformation).map(([key, value]) => {
                                        // Convert value to string if it's an object
                                        let displayValue: string;
                                        if (value === null || value === undefined) {
                                          displayValue = 'N/A';
                                        } else if (typeof value === 'object') {
                                          displayValue = JSON.stringify(value);
                                        } else {
                                          displayValue = String(value);
                                        }
                                        return (
                                          <div key={key} className="text-gray-700 text-xs">
                                            {key}: {displayValue}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* File Notes */}
                          <div className="mt-1.5">
                            <label className="block text-xs font-medium text-gray-700 mb-0.5">
                              Notes (optional)
                            </label>
                            <textarea
                              value={fileNote || ''}
                              onChange={(e) => {
                                setFileNotes(prev => {
                                  const updated = new Map(prev);
                                  if (e.target.value.trim()) {
                                    updated.set(file.name, e.target.value);
                                  } else {
                                    updated.delete(file.name);
                                  }
                                  return updated;
                                });
                              }}
                              placeholder="Add notes about this file..."
                              rows={2}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
              })}
            </div>
          )}

          {/* Next Due Date Review - Hide for custom/ad-hoc tasks (one-time tasks) */}
          {task.templateId && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs shadow-sm space-y-2">
              <div className="flex items-center gap-2 text-blue-800 font-medium uppercase tracking-wide">
                <Calendar className="w-3 h-3" />
                Next Due Date Review
              </div>
              {suggestedDueDate ? (
                <div className="text-gray-600">
                  AI detected an expiry date and recommends setting the next due date to{' '}
                  <strong>{suggestedDueDate.toLocaleDateString()}</strong> ({task.leadTimeDays || 30} days before expiry).
                  You can accept it, change it, or fall back to the template frequency.
                </div>
              ) : (
                <div className="text-gray-600">
                  No expiry date was detected in the uploaded documents. You can set the next due date manually or let Complin calculate it automatically using the template frequency.
                </div>
              )}
              <div className="space-y-1.5 text-gray-700">
                {suggestedDueDate && (
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="due-date-selection"
                      value="ai"
                      checked={dueDateSelection === 'ai'}
                      onChange={() => handleDueDateSelectionChange('ai')}
                    />
                    <span>Use AI suggested due date ({suggestedDueDate.toLocaleDateString()})</span>
                  </label>
                )}
                <label className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="due-date-selection"
                      value="manual"
                      checked={dueDateSelection === 'manual'}
                      onChange={() => handleDueDateSelectionChange('manual')}
                    />
                    <span>Set next due date manually</span>
                  </span>
                  {dueDateSelection === 'manual' && (
                    <div className="flex flex-col gap-1">
                      <input
                        type="date"
                        value={manualDueDateInput}
                        onChange={(event) => {
                          const selectedDate = event.target.value;
                          if (maxDateInput && selectedDate > maxDateInput) {
                            // Prevent selecting date after calculated frequency date
                            return;
                          }
                          setManualDueDateInput(selectedDate);
                        }}
                        max={maxDateInput}
                        className="border border-gray-300 rounded px-2 py-1 mt-1 w-fit"
                        required={dueDateSelection === 'manual'}
                      />
                      {maxDateInput && (
                        <div className="text-gray-500 text-[11px]">
                          Maximum date: {new Date(maxDateInput + 'T00:00:00').toLocaleDateString()} (based on template frequency)
                        </div>
                      )}
                    </div>
                  )}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="due-date-selection"
                    value="auto"
                    checked={dueDateSelection === 'auto'}
                    onChange={() => handleDueDateSelectionChange('auto')}
                  />
                  <span>
                    Let Complin calculate automatically using template frequency
                    {calculatedFrequencyDueDate && (
                      <span className="ml-2 font-medium text-blue-700">
                        (Next due date: {calculatedFrequencyDueDate.toLocaleDateString()})
                      </span>
                    )}
                  </span>
                </label>
              </div>
            </div>
          )}

          {requiredEvidenceList.length > 0 && relatedAssets.length === 0 && !hasRequiredEvidenceGeneral && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-orange-600">
              <AlertCircle className="w-3 h-3" />
              <span>
                Please upload required evidence: {requiredEvidenceList.map(e => e.label).join(', ')}
              </span>
            </div>
          )}

          {/* Related Assets with Completed Work */}
          {relatedAssets.length > 0 && (
            <>
              <div className="border-t pt-3 mt-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-primary-600" />
                  Related Assets ({relatedAssets.length} {relatedAssets.length === 1 ? 'asset' : 'assets'})
                </h4>
                
                {isLoadingAssets ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-2">Loading assets and completions...</p>
                  </div>
                ) : (
                  renderAssetsTable()
                )}
              </div>
              {requiredEvidenceList.length > 0 && relatedAssets.length > 0 && !hasRequiredEvidenceForAssets && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-orange-600">
                  <AlertCircle className="w-3 h-3" />
                  <span>
                    Please upload required evidence for each asset: {requiredEvidenceList.map(e => e.label).join(', ')}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Agreement Run History */}
          {templateAgreements.length > 0 && (
            <div className="border-t pt-2 mt-2">
              <h4 className="text-xs font-medium text-gray-700 mb-1.5">
                Agreements - Past Signatures ({agreementRuns.length > 0 ? agreementRuns.length : 0} {agreementRuns.length === 1 ? 'signature' : 'signatures'})
              </h4>
              
              {isLoadingAgreementHistory ? (
                <div className="text-center py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-xs text-gray-500 mt-1">Loading...</p>
                </div>
              ) : agreementRuns.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs border-collapse">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-1.5 py-1 text-left border border-gray-200">Contract Name</th>
                          <th className="px-1.5 py-1 text-left border border-gray-200">Signer</th>
                          <th className="px-1.5 py-1 text-left border border-gray-200">Key Dates</th>
                          <th className="px-1.5 py-1 text-left border border-gray-200">AI Summary</th>
                          <th className="px-1.5 py-1 text-left border border-gray-200">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agreementRuns.map((run) => {
                          const agreement = templateAgreements.find(a => a.id === run.agreementId);
                          
                          // Robust date parsing function (same logic as used in filtering)
                          const parseSignedDate = (completion: AgreementCompletion): Date | null => {
                            let runDate: Date | null = null;
                            
                            // Try to get date from signedAt first
                            if (completion.signedAt) {
                              try {
                                // Handle Firestore Timestamp explicitly
                                if (completion.signedAt && typeof completion.signedAt === 'object') {
                                  // Check if it's a Firestore Timestamp with toDate
                                  if ('toDate' in completion.signedAt && typeof completion.signedAt.toDate === 'function') {
                                    runDate = completion.signedAt.toDate();
                                  }
                                  // Check if it has _seconds property (Timestamp format)
                                  else if ('_seconds' in completion.signedAt || 'seconds' in completion.signedAt) {
                                    const seconds = completion.signedAt._seconds || completion.signedAt.seconds || 0;
                                    const nanoseconds = completion.signedAt._nanoseconds || completion.signedAt.nanoseconds || 0;
                                    runDate = new Date(seconds * 1000 + nanoseconds / 1000000);
                                  }
                                  // Check if it's a serverTimestamp placeholder
                                  else if (completion.signedAt._methodName === 'serverTimestamp') {
                                    // Use clientSignedAt as fallback
                                    if (completion.clientSignedAt) {
                                      runDate = new Date(completion.clientSignedAt);
                                    }
                                  } else {
                                    // Try generic date conversion
                                    if (completion.signedAt instanceof Date) {
                                      runDate = completion.signedAt;
                                    } else if (typeof completion.signedAt === 'number') {
                                      runDate = completion.signedAt < 1000000000000 ? new Date(completion.signedAt * 1000) : new Date(completion.signedAt);
                                    } else if (typeof completion.signedAt === 'string') {
                                      runDate = new Date(completion.signedAt);
                                    }
                                  }
                                } else if (completion.signedAt instanceof Date) {
                                  runDate = completion.signedAt;
                                } else if (typeof completion.signedAt === 'number') {
                                  runDate = completion.signedAt < 1000000000000 ? new Date(completion.signedAt * 1000) : new Date(completion.signedAt);
                                } else if (typeof completion.signedAt === 'string') {
                                  runDate = new Date(completion.signedAt);
                                }
                              } catch (e) {
                                console.log('⚠️ Error parsing signedAt for display:', completion.id, e);
                              }
                            }
                            
                            // Fallback to clientSignedAt if signedAt didn't work
                            if ((!runDate || isNaN(runDate.getTime())) && completion.clientSignedAt) {
                              runDate = new Date(completion.clientSignedAt);
                            }
                            
                            return (runDate && !isNaN(runDate.getTime())) ? runDate : null;
                          };
                          
                          const signedDate = parseSignedDate(run);

                          // Parse dates for Key Dates column
                          const parseDate = (dateValue: any): Date | null => {
                            if (!dateValue) return null;
                            if (dateValue instanceof Date) return dateValue;
                            if (typeof dateValue === 'string') {
                              const parsed = new Date(dateValue);
                              return isNaN(parsed.getTime()) ? null : parsed;
                            }
                            if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
                              return dateValue.toDate();
                            }
                            return null;
                          };

                          const contractDate = parseDate(run.contractDate);
                          const expiryDate = parseDate(run.expiryDate);
                          
                          return (
                            <tr 
                              key={run.id} 
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => setSelectedAgreementRun(run)}
                            >
                              <td className="px-1.5 py-1 border border-gray-200 text-gray-700 font-medium">
                                {agreement?.name || run.agreementName || 'Unknown'}
                              </td>
                              <td className="px-1.5 py-1 border border-gray-200 text-gray-700">
                                {run.signerName || 'Unknown'}
                              </td>
                              <td className="px-1.5 py-1 border border-gray-200 text-gray-700">
                                <div className="space-y-0.5 text-xs">
                                  {contractDate ? (
                                    <div>Signed: {contractDate.toLocaleDateString()}</div>
                                  ) : signedDate ? (
                                    <div>Signed: {signedDate.toLocaleDateString()}</div>
                                  ) : null}
                                  {expiryDate && (
                                    <div className={`font-semibold ${
                                      (() => {
                                        const now = new Date();
                                        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                        if (daysUntilExpiry < 0) return 'text-red-600';
                                        if (daysUntilExpiry < 30) return 'text-orange-600';
                                        return 'text-green-600';
                                      })()
                                    }`}>
                                      Expires: {expiryDate.toLocaleDateString()}
                                    </div>
                                  )}
                                  {!contractDate && !signedDate && !expiryDate && (
                                    <div className="text-gray-400 italic">No dates</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-1.5 py-1 border border-gray-200 text-gray-700">
                                {run.contractSummary ? (
                                  <div className="text-xs max-w-xs" title={run.contractSummary}>
                                    {run.contractSummary.substring(0, 80)}...
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400 italic">No AI analysis</div>
                                )}
                              </td>
                              <td className="px-1.5 py-1 border border-gray-200">
                                {run.signedPdfUrl && (
                                  <a
                                    href={run.signedPdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:text-blue-800 text-xs"
                                  >
                                    <Eye className="w-3 h-3" />
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No past signatures found.</p>
              )}
            </div>
          )}

          {/* Electronic Signature */}
          <div className="border-t pt-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Electronic Signature <span className="text-gray-500 font-normal">(Optional)</span>
            </label>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Signer Name</label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder={user?.name || "Enter name"}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Signer Email</label>
                  <input
                    type="email"
                    value={signerEmail}
                    onChange={(e) => setSignerEmail(e.target.value)}
                    placeholder={user?.email || "Enter email"}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Signature</label>
                <SimpleSignatureCanvas
                  onSignatureChange={setSignatureDataUrl}
                  width={500}
                  height={200}
                  disabled={isSubmitting}
                />
              </div>
              {signatureDataUrl && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="w-3 h-3" />
                  <span>Signature captured</span>
                </div>
              )}
            </div>
          </div>

          {/* Comments and Notes - Side by Side */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={2}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                placeholder="Add comments..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                placeholder="Add notes..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="pt-2 border-t border-gray-200 space-y-2">
            {(isAnalyzing || pendingAnalyses.size > 0) && (
              <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded p-2">
                <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                <span>
                  AI analysis in progress... {pendingAnalyses.size > 0 
                    ? `Analyzing ${pendingAnalyses.size} file${pendingAnalyses.size > 1 ? 's' : ''}. Please wait before completing.`
                    : 'Please wait before completing.'}
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary px-4 py-2 text-sm"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || (requiredEvidenceList.length > 0 && !hasRequiredEvidence) || isAnalyzing || pendingAnalyses.size > 0}
                title={isAnalyzing || pendingAnalyses.size > 0 ? `AI analysis in progress... ${pendingAnalyses.size > 0 ? `(${pendingAnalyses.size} file${pendingAnalyses.size > 1 ? 's' : ''} remaining)` : ''}` : undefined}
              >
                {isSubmitting ? 'Completing...' : (isAnalyzing || pendingAnalyses.size > 0) ? `Analyzing... ${pendingAnalyses.size > 0 ? `(${pendingAnalyses.size})` : ''}` : 'Complete Task'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>

      {/* Checklist Run Details Panel */}
      {selectedRun && templateChecklist && (
        <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[230] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Checklist Run Details</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {selectedRun.completedAt?.toDate 
                  ? selectedRun.completedAt.toDate().toLocaleString()
                  : typeof selectedRun.completedAt === 'number'
                  ? new Date(selectedRun.completedAt).toLocaleString()
                  : 'Unknown date'}
              </p>
            </div>
            <button
              onClick={() => setSelectedRun(null)}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {/* Variables */}
            {selectedRun.variableValues && Object.keys(selectedRun.variableValues).length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                <div className="space-y-1">
                  {Object.entries(selectedRun.variableValues).map(([key, value]) => {
                    // Convert value to string if it's an object
                    let displayValue: string;
                    if (value === null || value === undefined) {
                      displayValue = 'N/A';
                    } else if (typeof value === 'object') {
                      displayValue = JSON.stringify(value);
                    } else {
                      displayValue = String(value);
                    }
                    return (
                      <div key={key} className="text-xs">
                        <span className="font-medium text-gray-700">{key}:</span>{' '}
                        <span className="text-gray-900">{displayValue}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Completion Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="space-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-700">Location:</span>{' '}
                  <span className="text-gray-900">{selectedRun.locationName || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Completed By:</span>{' '}
                  <span className="text-gray-900">{selectedRun.completedByName || selectedRun.completedBy || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* Checklist Items */}
            <div>
              <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                Checklist Items ({Object.values(selectedRun.items || {}).filter(v => v).length}/{templateChecklist.items?.length || 0} completed)
              </h4>
              <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[calc(100vh-250px)] overflow-y-auto">
                {templateChecklist.items?.map((item, index) => {
                  // Use items (mobile format)
                  const items = selectedRun.items || {};
                  const isCompleted = items[item.text] || false;
                  const photoData = selectedRun.photos?.[item.text];
                  // Normalize photos to array: handle both string (single photo) and string[] (multiple photos)
                  const photos = photoData 
                    ? (Array.isArray(photoData) ? photoData : [photoData])
                    : [];
                  const taskRefs = (selectedRun.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                  
                  return (
                    <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                      <div className="flex-shrink-0 mt-0.5">
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Circle className="w-4 h-4 text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">
                          <span className="font-medium">{index + 1}.</span> {item.text}
                        </div>
                        {isCompleted && taskRefs.length > 0 && (
                          <div className="mt-1 text-xs text-blue-600 font-medium">
                            ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                          </div>
                        )}
                        {photos.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-2">
                            {photos.map((photoUrl, photoIndex) => (
                              <img 
                                key={photoIndex}
                                src={photoUrl} 
                                alt={`${item.text} photo ${photoIndex + 1}`}
                                className="max-w-full h-auto rounded border border-gray-300 max-h-32"
                                onClick={() => window.open(photoUrl, '_blank')}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Run Details Panel */}
      {selectedFormRun && templateForm && (
        <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[230] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Form Run Details</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {selectedFormRun.completionDate?.toDate 
                  ? selectedFormRun.completionDate.toDate().toLocaleString()
                  : typeof selectedFormRun.completionDate === 'number'
                  ? new Date(selectedFormRun.completionDate).toLocaleString()
                  : 'Unknown date'}
              </p>
            </div>
            <button
              onClick={() => setSelectedFormRun(null)}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {/* Variables */}
            {selectedFormRun.variableValues && Object.keys(selectedFormRun.variableValues).length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                <div className="space-y-1">
                  {Object.entries(selectedFormRun.variableValues).map(([key, value]) => {
                    // Convert value to string if it's an object
                    let displayValue: string;
                    if (value === null || value === undefined) {
                      displayValue = 'N/A';
                    } else if (typeof value === 'object') {
                      displayValue = JSON.stringify(value);
                    } else {
                      displayValue = String(value);
                    }
                    return (
                      <div key={key} className="text-xs">
                        <span className="font-medium text-gray-700">{key}:</span>{' '}
                        <span className="text-gray-900">{displayValue}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* AI Summary */}
            {selectedFormRun.aiSummary && (
              <div className="bg-purple-50 border border-purple-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-purple-600" />
                  AI Summary
                </h4>
                <p className="text-xs text-gray-700 italic">{selectedFormRun.aiSummary}</p>
              </div>
            )}

            {/* Completion Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="space-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-700">Location:</span>{' '}
                  <span className="text-gray-900">{selectedFormRun.locationName || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Completed By:</span>{' '}
                  <span className="text-gray-900">{selectedFormRun.completedBy || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div>
              <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                Form Fields
              </h4>
              <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[400px] overflow-y-auto">
                {templateForm.fields?.map((field, index) => {
                  const fieldValue = selectedFormRun.fieldValues?.[field.label];
                  return (
                    <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs">
                          <span className="font-medium text-gray-700">{field.label}:</span>{' '}
                          <span className="text-gray-900">
                            {fieldValue !== null && fieldValue !== undefined && fieldValue !== '' 
                              ? String(fieldValue) 
                              : <span className="text-gray-400 italic">Not provided</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Run Details Panel */}
      {selectedAgreementRun && (
        <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[230] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Agreement Signature Details</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {(() => {
                  // Prefer AI-extracted contract date over upload date
                  const parseContractDate = (dateValue: any): Date | null => {
                    if (!dateValue) return null;
                    if (dateValue instanceof Date) {
                      return isNaN(dateValue.getTime()) ? null : dateValue;
                    }
                    if (typeof dateValue === 'string') {
                      const parsed = new Date(dateValue);
                      return isNaN(parsed.getTime()) ? null : parsed;
                    }
                    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
                      try {
                        const date = dateValue.toDate();
                        return isNaN(date.getTime()) ? null : date;
                      } catch (e) {
                        return null;
                      }
                    }
                    // Handle Firestore timestamp with _seconds
                    if (dateValue && typeof dateValue === 'object' && ('_seconds' in dateValue || 'seconds' in dateValue)) {
                      const seconds = dateValue._seconds || dateValue.seconds || 0;
                      const nanoseconds = dateValue._nanoseconds || dateValue.nanoseconds || 0;
                      return new Date(seconds * 1000 + nanoseconds / 1000000);
                    }
                    return null;
                  };
                  
                  const contractDate = parseContractDate(selectedAgreementRun.contractDate);
                  if (contractDate) {
                    return contractDate.toLocaleString();
                  }
                  
                  // Fallback to upload date if no contract date
                  const getDateFromValue = (value: any): Date | null => {
                    if (!value) return null;
                    if (value instanceof Date) {
                      return isNaN(value.getTime()) ? null : value;
                    }
                    // Handle serverTimestamp placeholder
                    if (value && typeof value === 'object' && value._methodName === 'serverTimestamp') {
                      // Use clientSignedAt as fallback
                      if (selectedAgreementRun.clientSignedAt) {
                        return new Date(selectedAgreementRun.clientSignedAt);
                      }
                      return null;
                    }
                    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                      try {
                        const date = value.toDate();
                        return isNaN(date.getTime()) ? null : date;
                      } catch (e) {
                        return null;
                      }
                    }
                    if (typeof value === 'number') {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    if (value && typeof value === 'object' && ('_seconds' in value || 'seconds' in value)) {
                      const seconds = value._seconds || value.seconds || 0;
                      const nanoseconds = value._nanoseconds || value.nanoseconds || 0;
                      return new Date(seconds * 1000 + nanoseconds / 1000000);
                    }
                    if (typeof value === 'string') {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    return null;
                  };
                  const signedDate = selectedAgreementRun.signedAt ? getDateFromValue(selectedAgreementRun.signedAt) : 
                                    (selectedAgreementRun.clientSignedAt ? new Date(selectedAgreementRun.clientSignedAt) : null);
                  return signedDate ? signedDate.toLocaleString() : 'Unknown date';
                })()}
              </p>
            </div>
            <button
              onClick={() => setSelectedAgreementRun(null)}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {/* Agreement Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="space-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-700">Agreement:</span>{' '}
                  <span className="text-gray-900">{selectedAgreementRun.agreementName || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Signer Name:</span>{' '}
                  <span className="text-gray-900">{selectedAgreementRun.signerName || 'Unknown'}</span>
                </div>
                {selectedAgreementRun.signerEmail && (
                  <div>
                    <span className="font-medium text-gray-700">Signer Email:</span>{' '}
                    <span className="text-gray-900">{selectedAgreementRun.signerEmail}</span>
                  </div>
                )}
              </div>
            </div>

            {/* AI Summary - Prominent Display */}
            <div className="bg-purple-50 border border-purple-200 rounded p-3">
              <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-purple-600" />
                AI Analysis Summary
              </h4>
              {selectedAgreementRun.contractSummary ? (
                <p className="text-xs text-gray-900 leading-relaxed">{selectedAgreementRun.contractSummary}</p>
              ) : (
                <p className="text-xs text-gray-500 italic">No AI analysis available for this contract.</p>
              )}
            </div>

            {/* AI-Parsed Contract Information */}
            {(selectedAgreementRun.contractSummary || selectedAgreementRun.contractDate || selectedAgreementRun.expiryDate || selectedAgreementRun.contractKeyInformation) && (
              <div className="bg-purple-50 border border-purple-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-purple-600" />
                  Contract Summary (AI-Parsed)
                </h4>
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    {selectedAgreementRun.expiryDate && (
                      <div>
                        <span className="font-medium text-gray-700">Expiry Date:</span>
                        <p className={`font-semibold ${
                          (() => {
                            const expiry = typeof selectedAgreementRun.expiryDate === 'string' 
                              ? new Date(selectedAgreementRun.expiryDate)
                              : selectedAgreementRun.expiryDate?.toDate?.() || selectedAgreementRun.expiryDate;
                            if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'text-gray-900';
                            const now = new Date();
                            const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                            if (daysUntilExpiry < 0) return 'text-red-600';
                            if (daysUntilExpiry < 30) return 'text-orange-600';
                            return 'text-green-600';
                          })()
                        }`}>
                          {(() => {
                            const expiry = typeof selectedAgreementRun.expiryDate === 'string' 
                              ? new Date(selectedAgreementRun.expiryDate)
                              : selectedAgreementRun.expiryDate?.toDate?.() || selectedAgreementRun.expiryDate;
                            if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'Not available';
                            const formatted = expiry.toLocaleDateString();
                            const now = new Date();
                            const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                            if (daysUntilExpiry < 0) return `${formatted} (Expired ${Math.abs(daysUntilExpiry)} days ago)`;
                            if (daysUntilExpiry < 30) return `${formatted} (Expires in ${daysUntilExpiry} days)`;
                            return formatted;
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                  {selectedAgreementRun.contractKeyInformation && Object.keys(selectedAgreementRun.contractKeyInformation).length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Key Information:</span>
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(selectedAgreementRun.contractKeyInformation).map(([key, value]) => {
                          // Convert value to string if it's an object
                          let displayValue: string;
                          if (value === null || value === undefined) {
                            displayValue = 'N/A';
                          } else if (typeof value === 'object') {
                            // If it's an object, stringify it nicely
                            try {
                              displayValue = JSON.stringify(value, null, 2);
                            } catch (e) {
                              displayValue = String(value);
                            }
                          } else {
                            displayValue = String(value);
                          }
                          
                          return (
                            <div key={key} className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                              <span className="text-gray-600 font-medium break-words min-w-0 sm:min-w-[100px] sm:flex-shrink-0">{key}:</span>
                              <span className="text-gray-900 whitespace-pre-wrap break-words min-w-0">{displayValue}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Signature Preview */}
            {selectedAgreementRun.signatureUrl && (
              <div className="bg-white border border-gray-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1">Signature</h4>
                <img 
                  src={selectedAgreementRun.signatureUrl} 
                  alt="Signature"
                  className="max-w-full h-auto rounded border border-gray-300 bg-white"
                  style={{ maxHeight: '150px' }}
                />
              </div>
            )}

            {/* Signed PDF Link */}
            {selectedAgreementRun.signedPdfUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-blue-600" />
                  Signed Document
                </h4>
                <a
                  href={selectedAgreementRun.signedPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  View Signed PDF
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* File Size Error Modal */}
      <FileSizeErrorModal
        fileName={fileSizeError?.fileName || ''}
        fileSize={fileSizeError?.fileSize || 0}
        isOpen={!!fileSizeError}
        onClose={() => setFileSizeError(null)}
      />

      {/* Full-Screen Reference Image Viewer */}
      {viewingReferenceImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[250] flex items-center justify-center"
          onClick={() => {
            setViewingReferenceImageUrl(null);
            setViewingReferenceImageUrls([]);
            setViewingReferenceImageIndex(0);
          }}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={() => {
                setViewingReferenceImageUrl(null);
                setViewingReferenceImageUrls([]);
                setViewingReferenceImageIndex(0);
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
              title="Close (Esc)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            {viewingReferenceImageUrls.length > 1 && viewingReferenceImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingReferenceImageIndex - 1;
                  setViewingReferenceImageIndex(newIndex);
                  setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
                }}
                className="absolute left-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Previous image (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Next Button */}
            {viewingReferenceImageUrls.length > 1 && viewingReferenceImageIndex < viewingReferenceImageUrls.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingReferenceImageIndex + 1;
                  setViewingReferenceImageIndex(newIndex);
                  setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
                }}
                className="absolute right-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Next image (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <img
              src={viewingReferenceImageUrl}
              alt={`Reference image ${viewingReferenceImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter and Note */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
              {viewingReferenceImageUrls.length > 1 && (
                <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                  {viewingReferenceImageIndex + 1} / {viewingReferenceImageUrls.length}
                </div>
              )}
              {/* Show note if available */}
              {task.referenceImageMetadata && viewingReferenceImageUrl && (() => {
                const currentImageMetadata = task.referenceImageMetadata.find(
                  meta => meta.url === viewingReferenceImageUrl
                );
                if (currentImageMetadata?.note) {
                  return (
                    <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm max-w-md text-center">
                      <div className="font-medium mb-1">Note:</div>
                      <div>{currentImageMetadata.note}</div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompleteTaskModal;

